import { getEventDef } from './events/registry.js';
import { NotificationEventDefinitionSchema } from './notification-event-definition.schema.js';
import {
    POLICY_OWNERSHIP_EVENT_SET,
    isPolicyOwnedNotificationEvent,
    listPolicyOwnedCrudResourceRows,
    listPolicyOwnedCustomResourceRows,
    listNotificationOwnershipRows,
} from './notification-ownership.registry.js';

const VERB_META = Object.freeze({
    created: {
        type: 'success',
        tr: 'oluşturuldu',
        en: 'created',
        navigationKind: 'listRowHighlight',
    },
    updated: {
        type: 'info',
        tr: 'güncellendi',
        en: 'updated',
        navigationKind: 'listRowHighlight',
    },
    deleted: {
        type: 'error',
        tr: 'silindi',
        en: 'deleted',
        navigationKind: 'listPageOnly',
    },
    completed: {
        type: 'success', tr: 'tamamlandı', en: 'completed', navigationKind: 'listRowHighlight',
    },
    submitted: {
        type: 'info', tr: 'gönderildi', en: 'submitted', navigationKind: 'listRowHighlight',
    },
    published: {
        type: 'success', tr: 'yayınlandı', en: 'published', navigationKind: 'listRowHighlight',
    },
    pending: {
        type: 'warning', tr: 'bekleyen değişiklik', en: 'pending changes', navigationKind: 'listRowHighlight',
    },
    applicantReceipt: {
        type: 'success', tr: 'başvuru alındı', en: 'applicant receipt sent', navigationKind: 'listPageOnly',
    },
    registered: { type: 'success', tr: 'kaydoldu', en: 'registered', navigationKind: 'listRowHighlight' },
    invited: { type: 'info', tr: 'davet edildi', en: 'invited', navigationKind: 'listRowHighlight' },
    passwordChanged: { type: 'warning', tr: 'parolası değişti', en: 'password changed', navigationKind: 'listRowHighlight' },
    login: { type: 'info', tr: 'giriş yaptı', en: 'logged in', navigationKind: 'listRowHighlight' },
    expiryReminder: { type: 'warning', tr: 'süresi yaklaşıyor', en: 'expiry approaching', navigationKind: 'listRowHighlight' },
    plannedReminder: { type: 'warning', tr: 'planı yaklaşıyor', en: 'planned date approaching', navigationKind: 'listRowHighlight' },
    supportEndReminder: { type: 'warning', tr: 'destek bitişi yaklaşıyor', en: 'support end approaching', navigationKind: 'listRowHighlight' },
    contractExpiryReminder: { type: 'warning', tr: 'sözleşme bitişi yaklaşıyor', en: 'contract expiry approaching', navigationKind: 'listRowHighlight' },
    reviewReminder: { type: 'warning', tr: 'inceleme yaklaşıyor', en: 'review approaching', navigationKind: 'listRowHighlight' },
    candidateRaised: { type: 'warning', tr: 'önerisi oluştu', en: 'suggestion raised', navigationKind: 'listPageOnly' },
    revoked: { type: 'warning', tr: 'iptal edildi', en: 'revoked', navigationKind: 'listRowHighlight' },
    declined: { type: 'warning', tr: 'reddedildi', en: 'declined', navigationKind: 'listRowHighlight' },
    expired: { type: 'warning', tr: 'süresi doldu', en: 'expired', navigationKind: 'listRowHighlight' },
});

const COMMON_RELATIONS = Object.freeze([
    {
        key: 'actor',
        label: { tr: 'İşlemi yapan', en: 'Event actor' },
        description: {
            tr: 'Olayı oluşturan aynı tenant üyesi kullanıcı.',
            en: 'The same-tenant user who produced the event.',
        },
        maxMembers: 1,
    },
]);

const ASSET_AUTHORIZATION_RELATION = Object.freeze({
    key: 'assetPartyInstance',
    label: { tr: 'Varlık yetkilendirme kaydı', en: 'Asset authorization record' },
    description: {
        tr: 'Yalnızca kod tarafından varlık satırı yetkilendirmesi için kullanılan tenant kapsamlı kayıt.',
        en: 'Tenant-scoped record used only by code for asset row authorization.',
    },
    maxMembers: 1,
});

// Some managed resources are tenant-scoped settings or compatibility redirects rather than
// row-administered collections. Keep them managed, but make their notification target
// page-only until a dedicated row surface exists.
const CRUD_PAGE_ONLY_ENTITIES = new Set([
    'company',
    'integrationConnector',
    'reportConfidentialityClass',
    'tenantDirectory',
    'verbis',
    // Nested under Objective detail tabs — no standalone list row surface yet.
    'kpi',
    'strategicInitiative',
]);

const CRUD_MANAGEMENT_AREA_BY_ENTITY = Object.freeze({
    applicationMethod: 'definitions',
    assetCategory: 'assetInventory',
    ticketCategory: 'definitions',
    documentType: 'definitions',
    asset: 'assetInventory',
    applicationRequestInformation: 'privacyCatalog',
    classification: 'definitions',
    transferType: 'privacyCatalog',
    retentionPeriod: 'privacyCatalog',
    processingPurpose: 'privacyCatalog',
    dataSubjectRequest: 'privacyCatalog',
    verbis: 'privacyCatalog',
    assetGroup: 'assetInventory',
    assetLicense: 'assetInventory',
    assetDisposal: 'assetInventory',
    incident: 'incidentCrud',
});

const MANAGED_CRUD_AUDIENCE_SELECTORS = Object.freeze([
    'user',
    'role',
    'unit',
    'title',
    'assignmentTemplate',
    'pageViewers',
]);
const MANAGED_SPECIALTY_AUDIENCE_SELECTORS = Object.freeze([
    'user',
    'role',
    'unit',
    'title',
    'assignmentTemplate',
    'eventRelation',
    'pageViewers',
]);

export const NOTIFICATION_POLICY_SYSTEM_VARIABLES = Object.freeze([
    {
        key: 'title',
        type: 'string',
        label: { tr: 'Başlık', en: 'Title' },
        description: {
            tr: 'Yayın anında oluşturulan bildirim başlığı.',
            en: 'Notification title materialised at publish time.',
        },
        example: 'Sample title',
        required: false,
    },
    {
        key: 'body',
        type: 'string',
        label: { tr: 'Gövde', en: 'Body' },
        description: {
            tr: 'Yayın anında oluşturulan bildirim gövdesi.',
            en: 'Notification body materialised at publish time.',
        },
        example: 'Sample body',
        required: false,
    },
    {
        key: 'tenantName',
        type: 'string',
        label: { tr: 'Kiracı adı', en: 'Tenant name' },
        description: {
            tr: 'Olayın ait olduğu kiracının adı.',
            en: 'Name of the tenant that owns the event.',
        },
        example: 'Demo Tenant',
        required: false,
    },
    {
        key: 'currentYear',
        type: 'number',
        label: { tr: 'Yıl', en: 'Current year' },
        description: {
            tr: 'Teslimat sırasındaki takvim yılı.',
            en: 'Calendar year at delivery time.',
        },
        example: 2026,
        required: false,
    },
]);

function stringField(key, label, { nullable = true, description } = {}) {
    return {
        key,
        type: 'string',
        label,
        description,
        allowedOperators: ['equals', 'notEquals', 'contains', 'isSet', 'isNotSet'],
        nullable,
        sensitivity: 'internal',
    };
}

function statusField() {
    return {
        key: 'status',
        type: 'enum',
        label: { tr: 'Durum', en: 'Status' },
        description: {
            tr: 'Kaydın aktif / pasif durumu.',
            en: 'Whether the record is active or inactive.',
        },
        allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
        enumValues: ['active', 'inactive'],
        nullable: false,
        sensitivity: 'internal',
    };
}

function resourceIdVariable(fixtureResourceId) {
    return {
        key: 'resourceId',
        type: 'string',
        label: { tr: 'Kayıt kimliği', en: 'Record id' },
        description: {
            tr: 'MongoDB kayıt kimliği. Tıklama hedefi navigation.entityId ile aynıdır; şablonda isteğe bağlı gösterilir.',
            en: 'MongoDB record id. Same value as navigation.entityId for clicks; optional in copy.',
        },
        example: fixtureResourceId,
        required: false,
        maxLength: 128,
    };
}

function variablesFromSafeFields(safeFields, fixtureFields, fixtureName, fixtureResourceId) {
    const nameVar = {
        key: 'name',
        type: 'string',
        label: { tr: 'Kayıt adı', en: 'Record name' },
        description: {
            tr: 'Bildirim metninde kullanılan görünen ad.',
            en: 'Display name used in notification copy.',
        },
        example: fixtureName,
        required: true,
        maxLength: 240,
    };
    const extras = safeFields
        .filter((field) => field.key !== 'name')
        .map((field) => ({
            key: field.key,
            type: field.type === 'enum' ? 'string' : field.type,
            label: field.label,
            description: field.description || {
                tr: `${field.label.tr} alanı.`,
                en: `${field.label.en} field.`,
            },
            example: fixtureFields?.[field.key] ?? null,
            required: !field.nullable,
            maxLength: field.type === 'string' || field.type === 'enum' ? 240 : undefined,
        }));
    return [nameVar, ...extras, resourceIdVariable(fixtureResourceId)];
}

function withFieldDescriptions(fields) {
    return fields.map((field) => ({
        ...field,
        description: field.description || {
            tr: `${field.label.tr} koşul alanı.`,
            en: `${field.label.en} condition field.`,
        },
    }));
}

const BASE_ENTITY_CONFIGS = Object.freeze([
    {
        entity: 'applicationMethod',
        label: { tr: 'Başvuru yöntemi', en: 'Application method' },
        module: 'definitions',
        category: 'applicationTracking',
        notificationCategory: 'applicationMethods',
        resource: {
            type: 'ApplicationMethod',
            pageKey: 'definitionsApplicationTrackingApplicationMethod',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/application-method.controller.js:createBaseController',
            resourceName: 'applicationMethod',
            durable: true,
        },
        contextAdapterKey: 'crud.applicationMethod.v1',
        nameField: 'method',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                description: {
                    tr: 'Başvuru yönteminin görünen adı.',
                    en: 'Display name of the application method.',
                },
            }),
            stringField('method', { tr: 'Yöntem', en: 'Method' }, {
                description: {
                    tr: 'Kanal / yöntem kodu (ör. email, web).',
                    en: 'Channel / method code (for example email, web).',
                },
            }),
            statusField(),
        ]),
        fixtureFields: { name: 'E-posta', method: 'email', status: 'active' },
        fixtureName: 'E-posta',
    },
    {
        entity: 'assetCategory',
        label: { tr: 'Varlık kategorisi', en: 'Asset category' },
        module: 'assetInventory',
        category: 'assetCategories',
        notificationCategory: 'assetCategories',
        resource: {
            type: 'AssetCategory',
            pageKey: 'assetInventoryAssetCategory',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/asset-category.controller.js:createBaseController',
            resourceName: 'assetCategory',
            durable: true,
        },
        contextAdapterKey: 'crud.assetCategory.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Varlık kategorisinin adı.',
                    en: 'Asset category name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Kategori kodu.',
                    en: 'Category code.',
                },
            }),
            statusField(),
            ...['confidentialityValue', 'integrityValue', 'availabilityValue'].map((key) => ({
                key,
                type: 'number',
                label: {
                    tr: key === 'confidentialityValue'
                        ? 'Gizlilik değeri'
                        : key === 'integrityValue'
                            ? 'Bütünlük değeri'
                            : 'Erişilebilirlik değeri',
                    en: key === 'confidentialityValue'
                        ? 'Confidentiality value'
                        : key === 'integrityValue'
                            ? 'Integrity value'
                            : 'Availability value',
                },
                description: {
                    tr: 'CIA skor alanı (1–5 aralığı tipik).',
                    en: 'CIA score field (typically 1–5).',
                },
                allowedOperators: [
                    'equals',
                    'greaterThan',
                    'greaterThanOrEqual',
                    'lessThan',
                    'lessThanOrEqual',
                    'between',
                ],
                nullable: true,
                sensitivity: 'internal',
            })),
        ]),
        fixtureFields: {
            name: 'Sunucu',
            code: 'SERVER',
            status: 'active',
            confidentialityValue: 4,
            integrityValue: 4,
            availabilityValue: 5,
        },
        fixtureName: 'Sunucu',
    },
    {
        entity: 'ticketCategory',
        label: { tr: 'Talep kategorisi', en: 'Ticket category' },
        module: 'ticketing',
        category: 'ticketCategory',
        notificationCategory: 'ticketCategory',
        resource: {
            type: 'TicketCategory',
            pageKey: 'ticketCategory',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/ticket-category.controller.js:createBaseController',
            resourceName: 'ticketCategory',
            durable: true,
        },
        contextAdapterKey: 'crud.ticketCategory.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Talep kategorisinin adı.',
                    en: 'Ticket category name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Kategori kodu.',
                    en: 'Category code.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: { name: 'Erişim', code: 'ACCESS', status: 'active' },
        fixtureName: 'Erişim',
    },
    {
        entity: 'documentType',
        label: { tr: 'Belge türü', en: 'Document type' },
        module: 'definitions',
        category: 'documentTypes',
        notificationCategory: 'documentTypes',
        resource: {
            type: 'DocumentType',
            pageKey: 'definitionsDocumentDocumentType',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/document-type.controller.js:createBaseController',
            resourceName: 'documentType',
            durable: true,
        },
        contextAdapterKey: 'crud.documentType.v1',
        nameField: 'documentCategory',
        safeFields: withFieldDescriptions([
            stringField('documentCategory', { tr: 'Belge kategorisi', en: 'Document category' }, {
                nullable: false,
                description: {
                    tr: 'Belge türünün kategori adı (görünen ad kaynağı).',
                    en: 'Document category name (display-name source).',
                },
            }),
            stringField('documentTypeAbbreviation', { tr: 'Kısaltma', en: 'Abbreviation' }, {
                nullable: false,
                description: {
                    tr: 'Belge türü kısaltması.',
                    en: 'Document type abbreviation.',
                },
            }),
            stringField('complianceCategory', { tr: 'Uyumluluk kategorisi', en: 'Compliance category' }, {
                nullable: false,
                description: {
                    tr: 'Uyumluluk sınıflandırması.',
                    en: 'Compliance classification.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: {
            documentCategory: 'Politika',
            documentTypeAbbreviation: 'POL',
            complianceCategory: 'ISO27001',
            status: 'active',
        },
        fixtureName: 'Politika',
    },
    {
        entity: 'asset',
        label: { tr: 'Varlık', en: 'Asset' },
        module: 'assetInventory',
        category: 'assets',
        notificationCategory: 'assets',
        resource: {
            type: 'Asset',
            pageKey: 'assetInventoryAssets',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/asset.controller.js:createBaseController',
            resourceName: 'asset',
            durable: true,
        },
        contextAdapterKey: 'crud.asset.v1',
        nameField: 'assetName',
        safeFields: withFieldDescriptions([
            stringField('assetName', { tr: 'Varlık adı', en: 'Asset name' }, {
                nullable: false,
                description: {
                    tr: 'Varlığın görünen adı.',
                    en: 'Display name of the asset.',
                },
            }),
            stringField('assetId', { tr: 'Envanter kodu', en: 'Inventory code' }, {
                description: {
                    tr: 'İnsan tarafından okunabilir envanter kodu (Mongo kimliği değil).',
                    en: 'Human-readable inventory code (not the Mongo record id).',
                },
            }),
            stringField('category', { tr: 'Kategori', en: 'Category' }, {
                description: {
                    tr: 'Varlık kategori etiketi.',
                    en: 'Asset category label.',
                },
            }),
        ]),
        fixtureFields: {
            assetName: 'Laptop-042',
            assetId: 'AST-042',
            category: 'Endpoint',
        },
        fixtureName: 'Laptop-042',
    },
    // Cohort 1 — low-risk definitions / KVKK catalog CRUD
    {
        entity: 'applicationRequestInformation',
        label: { tr: 'Başvuru talep bilgisi', en: 'Application request information' },
        module: 'definitions',
        category: 'applicationTracking',
        notificationCategory: 'applicationRequestInformation',
        resource: {
            type: 'ApplicationRequestInformation',
            pageKey: 'definitionsApplicationTrackingApplicationRequestInformation',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/application-request-information.controller.js:createBaseController',
            resourceName: 'applicationRequestInformation',
            durable: true,
        },
        contextAdapterKey: 'crud.applicationRequestInformation.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                description: {
                    tr: 'Talep kaydının adı.',
                    en: 'Name of the request catalog record.',
                },
            }),
            stringField('legalBasis', { tr: 'Hukuki dayanak', en: 'Legal basis' }, {
                description: {
                    tr: 'Başvuru için hukuki dayanak özeti.',
                    en: 'Legal basis summary for the request.',
                },
            }),
            stringField('requestSubject', { tr: 'Talep konusu', en: 'Request subject' }, {
                description: {
                    tr: 'Talep konusu / başlığı.',
                    en: 'Request subject / title.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: {
            name: 'Erişim talebi',
            legalBasis: 'KVKK Madde 11',
            requestSubject: 'Kişisel veri erişimi',
            status: 'active',
        },
        fixtureName: 'Erişim talebi',
    },
    {
        entity: 'classification',
        label: { tr: 'Sınıflandırma', en: 'Classification' },
        module: 'definitions',
        category: 'documentTypes',
        notificationCategory: 'classifications',
        resource: {
            type: 'Classification',
            pageKey: 'definitionsDocumentClassification',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/classification.controller.js:createBaseController',
            resourceName: 'classification',
            durable: true,
        },
        contextAdapterKey: 'crud.classification.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Sınıflandırma seviyesinin adı.',
                    en: 'Classification level name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Sınıflandırma kodu.',
                    en: 'Classification code.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: { name: 'Gizli', code: 'CONF', status: 'active' },
        fixtureName: 'Gizli',
    },
    {
        entity: 'transferType',
        label: { tr: 'Aktarım türü', en: 'Transfer type' },
        module: 'definitions',
        category: 'kvkkCatalog',
        notificationCategory: 'transferTypes',
        resource: {
            type: 'TransferType',
            pageKey: 'definitionsKvkkTransferType',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/transfer-type.controller.js:createBaseController',
            resourceName: 'transferType',
            durable: true,
        },
        contextAdapterKey: 'crud.transferType.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Aktarım türünün adı.',
                    en: 'Transfer type name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Aktarım türü kodu.',
                    en: 'Transfer type code.',
                },
            }),
            stringField('type', { tr: 'Tür', en: 'Type' }, {
                description: {
                    tr: 'yurtici / yurtdisi sınıflaması.',
                    en: 'Domestic / international classification.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: {
            name: 'Yurt içi aktarım',
            code: 'DOM',
            type: 'yurtici',
            status: 'active',
        },
        fixtureName: 'Yurt içi aktarım',
    },
    {
        entity: 'retentionPeriod',
        label: { tr: 'Saklama süresi', en: 'Retention period' },
        module: 'definitions',
        category: 'kvkkCatalog',
        notificationCategory: 'retentionPeriods',
        resource: {
            type: 'RetentionPeriod',
            pageKey: 'kvkkRetentionPeriod',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/retention-period.controller.js:createBaseController',
            resourceName: 'retentionPeriod',
            durable: true,
        },
        contextAdapterKey: 'crud.retentionPeriod.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Saklama süresi kaydının adı.',
                    en: 'Retention period name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Saklama süresi kodu.',
                    en: 'Retention period code.',
                },
            }),
            stringField('durationUnit', { tr: 'Süre birimi', en: 'Duration unit' }, {
                description: {
                    tr: 'days / months / years.',
                    en: 'days / months / years.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: {
            name: '7 yıl',
            code: 'Y7',
            durationUnit: 'years',
            status: 'active',
        },
        fixtureName: '7 yıl',
    },
    {
        entity: 'processingPurpose',
        label: { tr: 'İşleme amacı', en: 'Processing purpose' },
        module: 'definitions',
        category: 'kvkkCatalog',
        notificationCategory: 'processingPurposes',
        resource: {
            type: 'ProcessingPurpose',
            pageKey: 'definitionsKvkkProcessingPurpose',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/processing-purpose.controller.js:createBaseController',
            resourceName: 'processingPurpose',
            durable: true,
        },
        contextAdapterKey: 'crud.processingPurpose.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'İşleme amacının adı.',
                    en: 'Processing purpose name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'İşleme amacı kodu.',
                    en: 'Processing purpose code.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: { name: 'İnsan kaynakları', code: 'HR', status: 'active' },
        fixtureName: 'İnsan kaynakları',
    },
    // Cohort 2 — historical asset inventory CRUD definitions; delivery remains legacy.
    {
        entity: 'assetGroup',
        label: { tr: 'Varlık grubu', en: 'Asset group' },
        module: 'assetInventory',
        category: 'assetGroups',
        notificationCategory: 'assetGroups',
        resource: {
            type: 'AssetGroup',
            pageKey: 'assetInventoryAssetGroup',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/asset-group.controller.js:createBaseController',
            resourceName: 'assetGroup',
            durable: true,
        },
        contextAdapterKey: 'crud.assetGroup.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Varlık grubunun adı.',
                    en: 'Asset group name.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Varlık grubu kodu.',
                    en: 'Asset group code.',
                },
            }),
            stringField('category', { tr: 'Kategori', en: 'Category' }, {
                description: {
                    tr: 'Grup kategori etiketi.',
                    en: 'Group category label.',
                },
            }),
            stringField('assetType', { tr: 'Varlık tipi', en: 'Asset type' }, {
                description: {
                    tr: 'Donanım / yazılım / hizmet vb.',
                    en: 'Hardware / software / service etc.',
                },
            }),
            stringField('criticalityLevel', { tr: 'Kritiklik', en: 'Criticality' }, {
                description: {
                    tr: 'Grup kritiklik seviyesi.',
                    en: 'Group criticality level.',
                },
            }),
            statusField(),
        ]),
        fixtureFields: {
            name: 'Sunucu grubu',
            code: 'SRV-GRP',
            category: 'Altyapı',
            assetType: 'hardware',
            criticalityLevel: 'high',
            status: 'active',
        },
        fixtureName: 'Sunucu grubu',
    },
    {
        entity: 'assetLicense',
        label: { tr: 'Varlık lisansı', en: 'Asset license' },
        module: 'assetInventory',
        category: 'assetLicense',
        notificationCategory: 'assetLicense',
        resource: {
            type: 'AssetLicense',
            pageKey: 'assetInventoryLicenseManagement',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/asset-license.controller.js:createBaseController',
            resourceName: 'assetLicense',
            durable: true,
        },
        contextAdapterKey: 'crud.assetLicense.v1',
        safeFields: withFieldDescriptions([
            stringField('name', { tr: 'Ad', en: 'Name' }, {
                nullable: false,
                description: {
                    tr: 'Lisans kaydının adı.',
                    en: 'License record name.',
                },
            }),
            stringField('vendor', { tr: 'Tedarikçi', en: 'Vendor' }, {
                description: {
                    tr: 'Lisans sağlayıcı / tedarikçi adı.',
                    en: 'License vendor name.',
                },
            }),
            stringField('licenseType', { tr: 'Lisans tipi', en: 'License type' }, {
                description: {
                    tr: 'Abonelik / kalıcı / diğer tip etiketi.',
                    en: 'Subscription / perpetual / other type label.',
                },
            }),
            stringField('skuOrPartNo', { tr: 'SKU / parça no', en: 'SKU / part no' }, {
                description: {
                    tr: 'Ürün SKU veya parça numarası.',
                    en: 'Product SKU or part number.',
                },
            }),
            stringField('renewalType', { tr: 'Yenileme tipi', en: 'Renewal type' }, {
                description: {
                    tr: 'Yenileme periyodu (none / annual / monthly / …).',
                    en: 'Renewal period (none / annual / monthly / …).',
                },
            }),
            {
                key: 'status',
                type: 'enum',
                label: { tr: 'Durum', en: 'Status' },
                description: {
                    tr: 'Lisans kaydı durumu.',
                    en: 'License record status.',
                },
                allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
                enumValues: ['active', 'inactive', 'archived'],
                nullable: false,
                sensitivity: 'internal',
            },
        ]),
        fixtureFields: {
            name: 'Microsoft 365 E3',
            vendor: 'Microsoft',
            licenseType: 'subscription',
            skuOrPartNo: 'AAA-12345',
            renewalType: 'annual',
            status: 'active',
        },
        fixtureName: 'Microsoft 365 E3',
    },
    {
        entity: 'assetDisposal',
        label: { tr: 'Varlık imha kaydı', en: 'Asset disposal record' },
        module: 'assetInventory',
        category: 'assetDisposal',
        notificationCategory: 'assetDisposal',
        resource: {
            type: 'AssetDisposal',
            pageKey: 'assetInventoryDisposalManagement',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/asset-disposal.controller.js:createBaseController',
            resourceName: 'assetDisposal',
            durable: true,
        },
        contextAdapterKey: 'crud.assetDisposal.v1',
        nameField: 'title',
        safeFields: withFieldDescriptions([
            stringField('title', { tr: 'Başlık', en: 'Title' }, {
                nullable: false,
                description: {
                    tr: 'İmha / hurda kaydının başlığı.',
                    en: 'Disposal record title.',
                },
            }),
            stringField('kind', { tr: 'Tür', en: 'Kind' }, {
                description: {
                    tr: 'İmha / devir / geri dönüşüm türü.',
                    en: 'Decommission / destruction / transfer / recycle kind.',
                },
            }),
            stringField('destructionMethod', { tr: 'İmha yöntemi', en: 'Destruction method' }, {
                description: {
                    tr: 'Fiziksel / mantıksal imha yöntemi etiketi.',
                    en: 'Physical / logical destruction method label.',
                },
            }),
            stringField('certificateId', { tr: 'Sertifika no', en: 'Certificate id' }, {
                description: {
                    tr: 'İmha sertifikası referans numarası.',
                    en: 'Destruction certificate reference number.',
                },
            }),
            {
                key: 'status',
                type: 'enum',
                label: { tr: 'Durum', en: 'Status' },
                description: {
                    tr: 'İmha süreci durumu.',
                    en: 'Disposal workflow status.',
                },
                allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
                enumValues: ['planned', 'inProgress', 'completed', 'certified'],
                nullable: false,
                sensitivity: 'internal',
            },
        ]),
        fixtureFields: {
            title: '2026 Q1 disk imhası',
            kind: 'destruction',
            destructionMethod: 'physical_shred',
            certificateId: 'CERT-2026-001',
            status: 'planned',
        },
        fixtureName: '2026 Q1 disk imhası',
    },
    {
        entity: 'incident',
        label: { tr: 'Olay', en: 'Incident' },
        module: 'incident',
        category: 'incidents',
        notificationCategory: 'incidents',
        resource: {
            type: 'Incident',
            pageKey: 'incidentManagementIncidents',
        },
        producer: {
            kind: 'baseController',
            source: 'apps/api/src/controllers/incident.controller.js:createBaseController',
            resourceName: 'incident',
            durable: true,
        },
        contextAdapterKey: 'crud.incident.v1',
        nameField: 'title',
        safeFields: withFieldDescriptions([
            stringField('title', { tr: 'Başlık', en: 'Title' }, {
                nullable: false,
                description: {
                    tr: 'Olayın başlığı.',
                    en: 'Incident title.',
                },
            }),
            stringField('code', { tr: 'Kod', en: 'Code' }, {
                description: {
                    tr: 'Olay referans kodu.',
                    en: 'Incident reference code.',
                },
            }),
            {
                key: 'status',
                type: 'enum',
                label: { tr: 'Durum', en: 'Status' },
                description: {
                    tr: 'Olay yaşam döngüsü durumu.',
                    en: 'Incident lifecycle status.',
                },
                allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
                enumValues: [
                    'draft',
                    'returned',
                    'submitted',
                    'in_approval',
                    'approved',
                    'assigned',
                    'in_progress',
                    'resolved',
                    'closed',
                    'rejected',
                    'cancelled',
                ],
                nullable: false,
                sensitivity: 'internal',
            },
            {
                key: 'severity',
                type: 'enum',
                label: { tr: 'Önem derecesi', en: 'Severity' },
                description: {
                    tr: 'Olay önem derecesi.',
                    en: 'Incident severity.',
                },
                allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
                enumValues: ['critical', 'high', 'medium', 'low'],
                nullable: false,
                sensitivity: 'internal',
            },
            {
                key: 'impact',
                type: 'enum',
                label: { tr: 'Etki', en: 'Impact' },
                description: {
                    tr: 'Olay etkisi.',
                    en: 'Incident impact.',
                },
                allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
                enumValues: ['critical', 'high', 'medium', 'low'],
                nullable: false,
                sensitivity: 'internal',
            },
            {
                key: 'urgency',
                type: 'enum',
                label: { tr: 'Aciliyet', en: 'Urgency' },
                description: {
                    tr: 'Olay aciliyeti.',
                    en: 'Incident urgency.',
                },
                allowedOperators: ['equals', 'notEquals', 'in', 'notIn'],
                enumValues: ['critical', 'high', 'medium', 'low'],
                nullable: false,
                sensitivity: 'internal',
            },
            {
                key: 'slaBreached',
                type: 'boolean',
                label: { tr: 'SLA İhlali', en: 'SLA Breached' },
                description: {
                    tr: 'Olayda SLA süresinin aşılıp aşılmadığı.',
                    en: 'Whether the SLA has been breached for the incident.',
                },
                allowedOperators: ['equals', 'notEquals'],
                nullable: false,
                sensitivity: 'internal',
            },
        ]),
        fixtureFields: {
            title: 'Hizmet kesintisi',
            code: 'INC-001',
            status: 'in_progress',
            severity: 'high',
            impact: 'medium',
            urgency: 'high',
            slaBreached: false,
        },
        fixtureName: 'Hizmet kesintisi',
    },
]);

function controllerFileFor(entity) {
    if (entity === 'disposalRun') return 'disposal';
    return entity.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}

const GENERIC_CRUD_NAME_FIELDS = Object.freeze({
    action: 'title',
    user: 'email',
    threat: 'threatName',
    scenarioTimeline: 'phase',
    exerciseEvaluation: 'category',
    strategicInitiative: 'title',
});

const GENERIC_CRUD_CONFIGS = listPolicyOwnedCrudResourceRows()
    .filter(({ resourceModel }) => !BASE_ENTITY_CONFIGS.some(({ resource }) => resource.type === resourceModel))
    .map((row) => {
        const label = row.event.split('.')[0];
        return {
            entity: label,
            label: { tr: label, en: label },
            module: 'tenantDomain',
            category: label,
            notificationCategory: label,
            contextAdapterKey: `crud.${label}.v1`,
            resource: { type: row.resourceModel, pageKey: row.pageKey },
            producer: {
                kind: row.producerKind === 'baseController' ? 'baseController' : 'explicit',
                source: row.producerSource || `apps/api/src/controllers/${controllerFileFor(label)}.controller.js`,
                resourceName: label,
                durable: true,
            },
            safeFields: label === 'action' ? [
                stringField('name', { tr: 'Başlık', en: 'Title' }, { nullable: false }),
                { key: 'actionType', type: 'enum', label: { tr: 'Aksiyon türü', en: 'Action type' }, allowedOperators: ['equals', 'in'], enumValues: ['risk_treatment', 'corrective', 'preventive', 'improvement'], nullable: false, sensitivity: 'internal' },
            ] : [stringField('name', { tr: 'Görünen ad', en: 'Display name' }, { nullable: false })],
            fixtureFields: { name: `fixture-${label}`, ...(label === 'action' ? { actionType: 'risk_treatment' } : {}) },
            fixtureName: `fixture-${label}`,
            ...(GENERIC_CRUD_NAME_FIELDS[label] ? { nameField: GENERIC_CRUD_NAME_FIELDS[label] } : {}),
        };
    });
const ENTITY_CONFIGS = Object.freeze([...BASE_ENTITY_CONFIGS, ...GENERIC_CRUD_CONFIGS]);
const CRUD_VERBS = Object.freeze(['created', 'updated', 'deleted']);

const DSR_APPLICANT_RECEIPT_CONFIG = Object.freeze({
    safeFields: [
        stringField('referenceNumber', { tr: 'Referans numarası', en: 'Reference number' }, { nullable: false }),
        {
            key: 'deadline', type: 'date', label: { tr: 'Yasal son tarih', en: 'Legal deadline' },
            description: { tr: 'Başvurunun yasal cevap son tarihi.', en: 'Legal response deadline for the request.' },
            allowedOperators: ['equals', 'isSet', 'isNotSet'], nullable: true, sensitivity: 'internal',
        },
    ],
    fixtureFields: { referenceNumber: 'ORG-DSR-0001', deadline: '2026-02-01T00:00:00.000Z' },
    fixtureName: 'ORG-DSR-0001',
    variables: [
        {
            key: 'name', type: 'string', label: { tr: 'Görünen ad', en: 'Display name' },
            example: 'ORG-DSR-0001', required: true, maxLength: 240,
        },
        {
            key: 'referenceNumber', type: 'string', label: { tr: 'Referans numarası', en: 'Reference number' },
            example: 'ORG-DSR-0001', required: true, maxLength: 240,
        },
        {
            key: 'deadline', type: 'date', label: { tr: 'Yasal son tarih', en: 'Legal deadline' },
            example: '2026-02-01T00:00:00.000Z', required: false,
        },
        {
            key: 'applicantName', type: 'string', label: { tr: 'Başvuru sahibi adı', en: 'Applicant name' },
            example: 'Ayşe Yılmaz', required: false, maxLength: 240,
        },
    ],
    runtimeOnlyVariables: ['applicantName'],
    externalRecipientKinds: ['dataSubjectRequest.applicant'],
    externalChannels: ['email'],
    audienceSelectors: ['eventRelation'],
    defaultPolicy: {
        audience: { include: [{ type: 'eventRelation', relation: 'actor' }], exclude: [] },
        channels: ['email'],
        deliveryClass: 'informational',
        priority: 'normal',
        recipientCap: 1,
    },
    channelPolicy: { required: ['email'], allowed: ['email'] },
});

const USER_LIFECYCLE_EVENT_CONFIGS = Object.freeze({
    'user.registered': {
        variables: [
            { key: 'name', type: 'string', label: { tr: 'Kullanıcı', en: 'User' }, example: 'ada.yilmaz', required: true, maxLength: 240 },
            { key: 'userName', type: 'string', label: { tr: 'Kullanıcı adı', en: 'Username' }, example: 'ada.yilmaz', required: false, maxLength: 240 },
            { key: 'firstName', type: 'string', label: { tr: 'Ad', en: 'First name' }, example: 'Ada', required: false, maxLength: 240 },
            resourceIdVariable('fixture-user'),
        ],
    },
    'user.invited': {
        variables: [
            { key: 'name', type: 'string', label: { tr: 'Kullanıcı', en: 'User' }, example: 'ada.yilmaz', required: true, maxLength: 240 },
            { key: 'userName', type: 'string', label: { tr: 'Kullanıcı adı', en: 'Username' }, example: 'ada.yilmaz', required: false, maxLength: 240 },
            { key: 'firstName', type: 'string', label: { tr: 'Ad', en: 'First name' }, example: 'Ada', required: false, maxLength: 240 },
            resourceIdVariable('fixture-user'),
        ],
    },
    'user.passwordChanged': {
        variables: [
            { key: 'name', type: 'string', label: { tr: 'Kullanıcı', en: 'User' }, example: 'ada.yilmaz', required: true, maxLength: 240 },
            { key: 'source', type: 'string', label: { tr: 'Kaynak', en: 'Source' }, example: 'self', required: false, maxLength: 80 },
            { key: 'changedAt', type: 'date', label: { tr: 'Değişim zamanı', en: 'Changed at' }, example: '2026-08-01T12:05:00.000Z', required: false },
            resourceIdVariable('fixture-user'),
        ],
    },
    'auth.login': {
        variables: [
            { key: 'name', type: 'string', label: { tr: 'Kullanıcı', en: 'User' }, example: 'ada.example.test', required: true, maxLength: 240 },
            { key: 'email', type: 'string', label: { tr: 'E-posta', en: 'Email' }, example: 'ada.example.test', required: false, maxLength: 240 },
            { key: 'loginAt', type: 'date', label: { tr: 'Giriş zamanı', en: 'Login time' }, example: '2026-08-01T12:00:00.000Z', required: false },
            { key: 'ipAddress', type: 'string', label: { tr: 'IP adresi', en: 'IP address' }, example: '203.0.113.10', required: false, maxLength: 80 },
            { key: 'authMethod', type: 'string', label: { tr: 'Kimlik yöntemi', en: 'Auth method' }, example: 'password', required: false, maxLength: 80 },
            resourceIdVariable('fixture-user'),
        ],
    },
});

const DELEGATION_LIFECYCLE_NOTIFY = Object.freeze({
    contextAdapterKey: 'delegation.lifecycle.v1',
    relations: [{
        key: 'recipient',
        label: { tr: 'Vekalet tarafları', en: 'Delegation parties' },
        description: { tr: 'Aynı tenant asil ve vekil kullanıcılar.', en: 'The same-tenant principal and delegatee.' },
        maxMembers: 2,
    }],
    audienceSelectors: ['eventRelation'],
    defaultPolicy: {
        audience: { include: [{ type: 'eventRelation', relation: 'recipient' }], exclude: [] },
        channels: ['in-app'],
        deliveryClass: 'informational',
        priority: 'normal',
        recipientCap: 2,
    },
    channelPolicy: { required: ['in-app'], allowed: ['in-app'] },
});

const DELEGATION_EVENT_CONFIGS = Object.freeze({
    'delegation.created': {
        contextAdapterKey: 'delegation.created.v1',
        relations: [{
            key: 'recipient',
            label: { tr: 'Vekil kullanıcı', en: 'Delegatee' },
            description: { tr: 'Vekalette belirtilen aynı tenant vekil kullanıcı.', en: 'The same-tenant delegatee named by the delegation.' },
            maxMembers: 1,
        }],
        audienceSelectors: ['eventRelation'],
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipient' }], exclude: [] },
            channels: ['in-app'],
            deliveryClass: 'informational',
            priority: 'normal',
            recipientCap: 1,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app'] },
    },
    'delegation.revoked': DELEGATION_LIFECYCLE_NOTIFY,
    'delegation.declined': DELEGATION_LIFECYCLE_NOTIFY,
    'delegation.expired': DELEGATION_LIFECYCLE_NOTIFY,
});

const ASSET_LIFECYCLE_REMINDER_DELIVERY = Object.freeze({
    defaultPolicy: {
        audience: { include: [{ type: 'pageViewers' }], exclude: [] },
        channels: ['in-app', 'email'],
        deliveryClass: 'informational',
        priority: 'normal',
        recipientCap: 500,
    },
    channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email'] },
});

const ASSET_LIFECYCLE_REMINDER_CONFIGS = Object.freeze({
    'assetLicense.expiryReminder': { contextAdapterKey: 'crud.assetLicense.v1', ...ASSET_LIFECYCLE_REMINDER_DELIVERY },
    'assetDisposal.plannedReminder': { contextAdapterKey: 'crud.assetDisposal.v1', ...ASSET_LIFECYCLE_REMINDER_DELIVERY },
    'asset.supportEndReminder': { contextAdapterKey: 'crud.asset.v1', ...ASSET_LIFECYCLE_REMINDER_DELIVERY },
    'vendor.contractExpiryReminder': { contextAdapterKey: 'crud.vendor.v1', ...ASSET_LIFECYCLE_REMINDER_DELIVERY },
    'vendorAssessment.reviewReminder': { contextAdapterKey: 'crud.vendorAssessment.v1', ...ASSET_LIFECYCLE_REMINDER_DELIVERY },
});

const PROBLEM_CANDIDATE_CONFIGS = Object.freeze({
    'problem.candidateRaised': {
        contextAdapterKey: 'crud.problemCandidate.v1',
        audienceSelectors: MANAGED_CRUD_AUDIENCE_SELECTORS,
        defaultPolicy: {
            audience: { include: [{ type: 'pageViewers' }], exclude: [] },
            channels: ['in-app'],
            deliveryClass: 'informational',
            priority: 'normal',
            recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email'] },
    },
});

const CUSTOM_CONFIGS = listPolicyOwnedCustomResourceRows().map((row) => {
    const lifecycleConfig = USER_LIFECYCLE_EVENT_CONFIGS[row.event];
    const specialConfig = DELEGATION_EVENT_CONFIGS[row.event]
        || ASSET_LIFECYCLE_REMINDER_CONFIGS[row.event]
        || PROBLEM_CANDIDATE_CONFIGS[row.event];
    const isApplicantReceipt = row.event === 'dataSubjectRequest.applicantReceipt';
    return {
        entity: row.event.split('.')[0],
        verb: row.event.split('.')[1],
        label: { tr: row.event.split('.')[0], en: row.event.split('.')[0] },
        module: 'tenantDomain',
        category: row.event.split('.')[0],
        notificationCategory: row.event.split('.')[0],
        contextAdapterKey: lifecycleConfig
            ? 'user.lifecycle.v1'
            : specialConfig?.contextAdapterKey || `crud.${row.event.split('.')[0]}.v1`,
        resource: { type: row.resourceModel, pageKey: row.pageKey },
        producer: { kind: 'explicit', source: row.producerSource, resourceName: row.event.split('.')[0], durable: true },
        safeFields: isApplicantReceipt
            ? DSR_APPLICANT_RECEIPT_CONFIG.safeFields
            : [stringField('name', { tr: 'Görünen ad', en: 'Display name' }, { nullable: false })],
        fixtureFields: isApplicantReceipt
            ? DSR_APPLICANT_RECEIPT_CONFIG.fixtureFields
            : { name: `fixture-${row.event}` },
        fixtureName: isApplicantReceipt
            ? DSR_APPLICANT_RECEIPT_CONFIG.fixtureName
            : `fixture-${row.event}`,
        ...(isApplicantReceipt ? DSR_APPLICANT_RECEIPT_CONFIG : {}),
        ...(specialConfig || {}),
        ...(lifecycleConfig ? {
            ...lifecycleConfig,
            relations: [{
                key: 'recipient',
                label: { tr: 'Bildirim alıcısı', en: 'Notification recipient' },
                description: { tr: 'Olaydaki aynı tenant kullanıcı.', en: 'The same-tenant user named by the event.' },
                maxMembers: 1,
            }],
            audienceSelectors: ['eventRelation'],
            defaultPolicy: {
                audience: { include: [{ type: 'eventRelation', relation: 'recipient' }], exclude: [] },
                channels: row.event === 'auth.login' ? ['in-app'] : ['email'],
                deliveryClass: 'informational',
                priority: 'normal',
                recipientCap: 1,
            },
            channelPolicy: row.event === 'auth.login'
                ? { required: ['in-app'], allowed: ['in-app'] }
                : { required: ['email'], allowed: ['email'] },
        } : {}),
    };
});

const nameFieldByEvent = new Map();

function definitionFor(config, verb) {
    const eventName = `${config.entity}.${verb}`;
    const verbMeta = VERB_META[verb];
    if (!getEventDef(eventName)) {
        throw new Error(`[notification-routing] Curated event is missing from EVENT_CATALOG: ${eventName}`);
    }

    const resourceId = `fixture-${config.entity}`;
    const variables = config.variables ? [...config.variables] : [
        ...variablesFromSafeFields(
            config.safeFields,
            config.fixtureFields,
            config.fixtureName,
            resourceId,
        ),
    ];
    if (!variables.some((entry) => entry.key === 'resourceId')) variables.push(resourceIdVariable(resourceId));
    const fixtureVariables = {
        ...(config.fixtureName ? { name: config.fixtureName } : {}),
        ...Object.fromEntries(variables.map((entry) => [entry.key, entry.example])),
        resourceId,
    };

    nameFieldByEvent.set(eventName, config.nameField || 'name');

    return NotificationEventDefinitionSchema.parse({
        name: eventName,
        version: 1,
        label: {
            tr: `${config.label.tr} ${verbMeta.tr}`,
            en: `${config.label.en} ${verbMeta.en}`,
        },
        description: {
            tr: `${config.label.tr} ${verbMeta.tr} olayının güvenli ve tenant kapsamlı bildirimi. Yalnızca küratörlü alanlar koşul ve şablon değişkeni olarak kullanılabilir.`,
            en: `Safe tenant-scoped notification for the ${config.label.en.toLowerCase()} ${verbMeta.en} event. Only curated fields may be used in conditions and template variables.`,
        },
        module: config.module,
        category: config.category,
        notificationCategory: config.notificationCategory,
        management: {
            status: 'managed',
            area: CRUD_MANAGEMENT_AREA_BY_ENTITY[config.entity] || 'generalCrud',
            kind: verb === 'deleted' ? 'criticalCrud' : verb === 'pending' ? 'actionRequired' : 'importantState',
            priority: verb === 'deleted' || verb === 'pending' ? 'high' : 'normal',
        },
        customization: config.customization || 'full',
        allowTenantSuppression: false,
        ...(config.externalRecipientKinds ? { externalRecipientKinds: config.externalRecipientKinds } : {}),
        ...(config.externalChannels ? { externalChannels: config.externalChannels } : {}),
        ...(config.runtimeOnlyVariables ? { runtimeOnlyVariables: config.runtimeOnlyVariables } : {}),
        contextAdapterKey: config.contextAdapterKey,
        accessGuardKey: config.entity === 'asset' ? 'asset-record-access.v1' : 'page-view.v1',
        ...(CRUD_PAGE_ONLY_ENTITIES.has(config.entity) ? { navigationKind: 'listPageOnly' } : {}),
        resource: config.resource,
        ...(config.entity === 'action' ? { navigationProfiles: {
            risk_treatment: { pageKey: 'riskOperationsTreatment', category: 'riskTreatments' },
            corrective: { pageKey: 'incidentActions', category: 'incidentActions' },
            preventive: { pageKey: 'incidentActions', category: 'incidentActions' },
            improvement: { pageKey: 'incidentActions', category: 'incidentActions' },
        } } : {}),
        producer: config.entity === 'incident'
            ? {
                kind: 'explicit',
                source: verb === 'created'
                    ? 'apps/api/src/controllers/incident.controller.js:create'
                    : verb === 'updated'
                        ? 'apps/api/src/controllers/incident.controller.js:update;apps/api/src/lib/incident/entity-update-draft.service.js:applyIncidentUpdateDraft'
                        : 'apps/api/src/controllers/incident-lifecycle.controller.js:deleteIncidentById;apps/api/src/workflow/entity-lifecycle/incident/lifecycle-operation-executors.js:executeIncidentDeletion',
                resourceName: config.entity,
                durable: true,
            }
            : config.producer,
        safeFields: config.safeFields,
        relations: config.relations || (config.entity === 'asset'
            ? [...COMMON_RELATIONS, ASSET_AUTHORIZATION_RELATION]
            : COMMON_RELATIONS),
        audienceSelectors: config.audienceSelectors || MANAGED_CRUD_AUDIENCE_SELECTORS,
        variables,
        channelPolicy: config.channelPolicy || {
            required: ['in-app'],
            allowed: ['in-app', 'email', 'sms', 'push'],
        },
        defaultPolicy: config.defaultPolicy ? {
            ...config.defaultPolicy,
            content: {
                mode: 'eventDefault',
                type: verbMeta.type,
                ...config.defaultPolicy.content,
            },
        } : {
            audience: {
                include: [{ type: 'pageViewers' }],
                exclude: [],
            },
            content: {
                mode: 'eventDefault',
                type: verbMeta.type,
            },
            channels: ['in-app'],
            deliveryClass: verb === 'pending' ? 'actionable' : 'informational',
            priority: verb === 'deleted' || verb === 'pending' ? 'high' : 'normal',
            recipientCap: 500,
        },
        maxRecipients: 500,
        previewFixture: {
            version: 1,
            event: {
                id: `fixture-${eventName}`,
                name: eventName,
                occurredAt: '2026-01-01T00:00:00.000Z',
                actorId: 'fixture-actor',
                correlationId: `fixture-${eventName}`,
                causationId: null,
            },
            tenant: { id: 'fixture-tenant' },
            resource: {
                type: config.resource.type,
                id: resourceId,
                pageKey: config.resource.pageKey,
            },
            fields: config.fixtureFields,
            relations: config.entity === 'asset'
                ? { actor: ['fixture-actor'], assetPartyInstance: ['fixture-asset-party'] }
                : { actor: ['fixture-actor'] },
            ...(config.externalRecipientKinds ? {
                externalRecipients: config.externalRecipientKinds.map((kind) => ({ kind, resourceId })),
            } : {}),
            variables: fixtureVariables,
            navigation: {
                pageKey: config.resource.pageKey,
                entityId: verb === 'deleted' ? null : resourceId,
                kind: verbMeta.navigationKind,
            },
        },
    });
}

const historicalDefinitions = ENTITY_CONFIGS.flatMap((config) => (
    CRUD_VERBS.map((verb) => definitionFor(config, verb))
));
const customDefinitions = CUSTOM_CONFIGS.map((config) => definitionFor(config, config.verb));

const DETAIL_VERB_META = Object.freeze({
    linked: { type: 'info', tr: 'bağlandı', en: 'linked' },
    unlinked: { type: 'warning', tr: 'bağlantısı kaldırıldı', en: 'unlinked' },
});
const DETAIL_OWNERSHIP_ROWS = listNotificationOwnershipRows().filter(({ family }) => family === 'detail');
const DETAIL_SAFE_FIELDS = Object.freeze([
    stringField('name', { tr: 'Kayıt adı', en: 'Record name' }, { nullable: false }),
]);

function detailDefinitionFor(row) {
    const [entity, suffix] = row.event.split(/\.(.+)/);
    const meta = DETAIL_VERB_META[
        suffix.endsWith('unlinked') || suffix.endsWith('Unlinked')
            ? 'unlinked'
            : (suffix.endsWith('linked') || suffix.endsWith('Linked') || suffix === 'linked' ? 'linked' : 'unlinked')
    ] || DETAIL_VERB_META.linked;
    const resourceId = `fixture-${entity}-detail`;
    const includesRoleHolders = row.detail.roleHolderRecipients === true;
    const definition = {
        name: row.event,
        version: 1,
        label: { tr: `${entity} ${meta.tr}`, en: `${entity} ${meta.en}` },
        description: { tr: 'Tenant kapsamlı detay ilişki bildirimi.', en: 'Tenant-scoped detail association notification.' },
        module: 'tenantDomain', category: entity, notificationCategory: entity,
        management: { status: 'managed', area: 'generalCrud', kind: 'importantState', priority: 'normal' },
        customization: 'full', allowTenantSuppression: false,
        contextAdapterKey: 'crud.detail.association.v1', accessGuardKey: row.guardProfile,
        resource: { type: row.resourceModel, pageKey: row.pageKey },
        detail: row.detail,
        producer: { kind: 'explicit', source: row.producerSource || `apps/api/src/lib/notification/${entity}-detail-notify.js`, resourceName: entity, durable: true },
        safeFields: DETAIL_SAFE_FIELDS, relations: [...COMMON_RELATIONS, ...(includesRoleHolders ? [{
            key: 'roleHolders', label: { tr: 'BCP rol sorumluları', en: 'BCP role holders' },
            description: { tr: 'İlgili BCP rollerinin birincil ve alternatif kullanıcıları.', en: 'Primary and alternate users of the related BCP roles.' }, maxMembers: 500,
        }] : [{
            key: 'detailLinked', label: { tr: 'Bağlı kayıtlar', en: 'Linked records' },
            description: { tr: 'Tenant kapsamlı ve sınırlandırılmış bağlı kayıt kimlikleri.', en: 'Tenant-scoped bounded linked record identifiers.' }, maxMembers: 500,
        }])],
        audienceSelectors: includesRoleHolders ? MANAGED_SPECIALTY_AUDIENCE_SELECTORS : MANAGED_CRUD_AUDIENCE_SELECTORS,
        variables: [
            ...variablesFromSafeFields(DETAIL_SAFE_FIELDS, { name: `fixture-${entity}` }, `fixture-${entity}`, resourceId),
            {
                key: 'count', type: 'number', label: { tr: 'Sayı', en: 'Count' },
                description: { tr: 'İlişki kapsamındaki kayıt sayısı.', en: 'Number of records in the association.' },
                example: 1, required: false, maxLength: 20,
            },
        ],
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email', 'sms', 'push'] },
        defaultPolicy: {
            audience: {
                include: [
                    { type: 'pageViewers' },
                    ...(includesRoleHolders ? [{ type: 'eventRelation', relation: 'roleHolders' }] : []),
                ],
                exclude: [],
            },
            content: { mode: 'eventDefault', type: meta.type },
            channels: ['in-app'], deliveryClass: 'informational', priority: 'normal', recipientCap: 500,
        },
        maxRecipients: 500,
        previewFixture: {
            version: 1,
            event: { id: `fixture-${row.event}`, name: row.event, occurredAt: '2026-01-01T00:00:00.000Z', actorId: 'fixture-actor', correlationId: `fixture-${row.event}`, causationId: null },
            tenant: { id: 'fixture-tenant' }, resource: { type: row.resourceModel, id: resourceId, pageKey: row.pageKey },
            fields: { name: `fixture-${entity}` }, relations: {
                actor: ['fixture-actor'],
                ...(includesRoleHolders ? { roleHolders: ['fixture-role-holder'] } : {}),
            }, variables: { name: `fixture-${entity}`, resourceId, count: 1 },
            navigation: { pageKey: row.pageKey, entityId: resourceId, kind: 'detailTabHighlight', tab: row.detail.tab },
        },
    };
    return NotificationEventDefinitionSchema.parse(definition);
}

const detailDefinitions = DETAIL_OWNERSHIP_ROWS.map(detailDefinitionFor);

const SPECIALTY_DECISION_CONFIGS = Object.freeze([
    ...['approved', 'rejected', 'returned', 'delegated'].map((verb) => ({
        name: `ticket.${verb}`,
        label: {
            tr: verb === 'approved' ? 'Talep onaylandı' : verb === 'rejected' ? 'Talep reddedildi' : verb === 'returned' ? 'Talep iade edildi' : 'Talep onayı devredildi',
            en: verb === 'approved' ? 'Ticket approved' : verb === 'rejected' ? 'Ticket rejected' : verb === 'returned' ? 'Ticket returned' : 'Ticket approval delegated',
        },
        area: 'ticketOperations', kind: verb === 'delegated' ? 'actionRequired' : 'importantState',
        priority: verb === 'delegated' ? 'high' : 'normal', module: 'ticketing', category: 'ticketLifecycle',
        resource: { type: 'Ticket', pageKey: 'ticketing' },
        relation: verb === 'delegated' ? 'delegate' : verb === 'returned' ? 'requester' : 'stakeholders',
        relationLabel: verb === 'delegated'
            ? { tr: 'Devredilen kullanıcı', en: 'Delegate' }
            : verb === 'returned' ? { tr: 'Talep sahibi', en: 'Requester' } : { tr: 'İlgili taraflar', en: 'Stakeholders' },
        adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: verb === 'delegated'
            ? 'packages/workflow-engine/src/bridges/task-assigned-emit.js'
            : 'apps/api/src/listeners/ticket-workflow.listener.js', durable: true,
    })),
    {
        name: 'ticket.slaBreached', label: { tr: 'Talep SLA süresini aştı', en: 'Ticket SLA breached' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'ticketing', category: 'sla',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: 'packages/scheduled-jobs/src/ticket-sla-breach.js', durable: true,
    },
    {
        name: 'ticket.slaWarning', label: { tr: 'Talep SLA uyarısı', en: 'Ticket SLA warning' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'ticketing', category: 'sla',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: 'packages/scheduled-jobs/src/ticket-sla-warning.js', durable: true,
    },
    {
        name: 'ticket.escalated', label: { tr: 'Talep SLA eskalasyonu', en: 'Ticket SLA escalated' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'ticketing', category: 'sla',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: 'packages/scheduled-jobs/src/ticket-sla-escalation.js', durable: true,
    },
    {
        name: 'ticket.commented', label: { tr: 'Talebe yorum eklendi', en: 'Ticket commented' },
        area: 'ticketOperations', kind: 'importantState', priority: 'normal', module: 'ticketing', category: 'ticketLifecycle',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: 'apps/api/src/controllers/ticket-comment.controller.js', durable: true,
    },
    {
        name: 'bia.submitted', label: { tr: 'BIA onaya gönderildi', en: 'BIA submitted' },
        area: 'bcmOperations', kind: 'actionRequired', priority: 'high', module: 'bcm', category: 'bcmLifecycle',
        resource: { type: 'Bia', pageKey: 'bcmBia' }, relation: 'approvers',
        relationLabel: { tr: 'Onaylayanlar', en: 'Approvers' }, adapter: 'bia.lifecycle.v1', guard: 'page-view.v1',
        source: 'apps/api/src/controllers/bia.controller.js', durable: true,
    },
    {
        name: 'bia.completed', label: { tr: 'BIA onaylandı', en: 'BIA approved' },
        area: 'bcmOperations', kind: 'importantState', priority: 'normal', module: 'bcm', category: 'bcmLifecycle',
        resource: { type: 'Bia', pageKey: 'bcmBia' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'bia.lifecycle.v1', guard: 'page-view.v1',
        source: 'apps/api/src/lib/bcm/bcm-approval.js', durable: true,
    },
    {
        name: 'bcp.submitted', label: { tr: 'BCP onaya gönderildi', en: 'BCP submitted' },
        area: 'bcmOperations', kind: 'actionRequired', priority: 'high', module: 'bcm', category: 'bcmLifecycle',
        resource: { type: 'Bcp', pageKey: 'bcmBcp' }, relation: 'approvers',
        relationLabel: { tr: 'Onaylayanlar', en: 'Approvers' }, adapter: 'bcp.lifecycle.v1', guard: 'page-view.v1',
        source: 'apps/api/src/controllers/bcp.controller.js', durable: true,
    },
    {
        name: 'bcp.activated', label: { tr: 'BCP aktive edildi', en: 'BCP activated' },
        area: 'bcmOperations', kind: 'actionRequired', priority: 'high', module: 'bcm', category: 'bcmLifecycle',
        resource: { type: 'Bcp', pageKey: 'bcmBcp' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'bcp.lifecycle.v1', guard: 'page-view.v1',
        source: 'apps/api/src/lib/bcm/bcp-activation.js', durable: true,
    },
    {
        name: 'crisisEvent.declared', label: { tr: 'Kriz ilan edildi', en: 'Crisis declared' },
        area: 'bcmOperations', kind: 'actionRequired', priority: 'urgent', module: 'bcm', category: 'bcmLifecycle',
        resource: { type: 'CrisisEvent', pageKey: 'bcmCrisisEvent' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'crisisEvent.lifecycle.v1', guard: 'page-view.v1',
        source: 'apps/api/src/controllers/crisis-event.controller.js', durable: true,
    },
    {
        name: 'crisisEvent.resolved', label: { tr: 'Kriz çözüldü', en: 'Crisis resolved' },
        area: 'bcmOperations', kind: 'importantState', priority: 'normal', module: 'bcm', category: 'bcmLifecycle',
        resource: { type: 'CrisisEvent', pageKey: 'bcmCrisisEvent' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'crisisEvent.lifecycle.v1', guard: 'page-view.v1',
        source: 'apps/api/src/controllers/crisis-event.controller.js', durable: true,
    },
    ...['approved', 'rejected', 'returned', 'delegated'].map((verb) => ({
        name: `incident.${verb}`,
        label: {
            tr: verb === 'approved' ? 'Olay onaylandı' : verb === 'rejected' ? 'Olay reddedildi' : verb === 'returned' ? 'Olay iade edildi' : 'Olay onayı devredildi',
            en: verb === 'approved' ? 'Incident approved' : verb === 'rejected' ? 'Incident rejected' : verb === 'returned' ? 'Incident returned' : 'Incident approval delegated',
        },
        area: 'incidentOperations', kind: verb === 'delegated' ? 'actionRequired' : 'importantState',
        priority: verb === 'delegated' ? 'high' : 'normal', module: 'incident', category: 'incidentLifecycle',
        resource: { type: 'Incident', pageKey: 'incidentManagementIncidents' },
        relation: verb === 'delegated' ? 'delegate' : 'requester',
        relationLabel: verb === 'delegated'
            ? { tr: 'Devredilen kullanıcı', en: 'Delegate' } : { tr: 'Bildiren kullanıcı', en: 'Reporter' },
        adapter: 'incident.lifecycle.v1', guard: 'incident-access.v1',
        source: verb === 'delegated'
            ? 'packages/workflow-engine/src/bridges/task-assigned-emit.js'
            : 'apps/api/src/listeners/incident-workflow.listener.js', durable: true,
    })),
]);

const DSR_SLA_SAFE_FIELDS = [
    stringField('referenceNumber', { tr: 'Referans numarası', en: 'Reference number' }, { nullable: false }),
    {
        key: 'deadline', type: 'date', label: { tr: 'Yasal son tarih', en: 'Legal deadline' },
        description: { tr: 'Başvurunun yasal cevap son tarihi.', en: 'Legal response deadline for the request.' },
        allowedOperators: ['equals', 'isSet', 'isNotSet'], nullable: true, sensitivity: 'internal',
    },
    stringField('status', { tr: 'Durum', en: 'Status' }),
];
const DSR_SLA_VARIABLES = [
    {
        key: 'name', type: 'string', label: { tr: 'Referans numarası', en: 'Reference number' },
        example: 'ORG-DSR-0001', required: true, maxLength: 240,
    },
    {
        key: 'referenceNumber', type: 'string', label: { tr: 'Referans numarası', en: 'Reference number' },
        example: 'ORG-DSR-0001', required: true, maxLength: 240,
    },
    {
        key: 'deadline', type: 'date', label: { tr: 'Yasal son tarih', en: 'Legal deadline' },
        example: '2026-02-01T00:00:00.000Z', required: false,
    },
    {
        key: 'daysLeft', type: 'number', label: { tr: 'Kalan gün', en: 'Days left' },
        example: 5, required: false,
    },
];
const DSR_SLA_FIXTURE_FIELDS = {
    referenceNumber: 'ORG-DSR-0001',
    deadline: '2026-02-01T00:00:00.000Z',
    status: 'beklemede',
};

const SLA_DATE_OPERATORS = ['equals', 'isSet', 'isNotSet'];

function slaDateField(key, label, description) {
    return {
        key,
        type: 'date',
        label,
        description,
        allowedOperators: SLA_DATE_OPERATORS,
        nullable: true,
        sensitivity: 'internal',
    };
}

const BREACH_SLA_SAFE_FIELDS = [
    stringField('referenceNumber', { tr: 'Referans numarası', en: 'Reference number' }, { nullable: false }),
    stringField('title', { tr: 'Başlık', en: 'Title' }),
    slaDateField('authorityDeadline', { tr: 'Kurul son tarihi', en: 'Authority deadline' }, {
        tr: 'Kurul bildirim süresi son tarihi.', en: 'Authority notification deadline.',
    }),
    slaDateField('dataSubjectDeadline', { tr: 'İlgili kişi son tarihi', en: 'Data-subject deadline' }, {
        tr: 'İlgili kişi bildirim süresi son tarihi.', en: 'Data-subject notification deadline.',
    }),
    stringField('status', { tr: 'Durum', en: 'Status' }),
];
const BREACH_SLA_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'BR-2026-0001', required: true, maxLength: 240 },
    { key: 'referenceNumber', type: 'string', label: { tr: 'Referans numarası', en: 'Reference number' }, example: 'BR-2026-0001', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'Veri olayı', required: false, maxLength: 240 },
    { key: 'authorityDeadline', type: 'date', label: { tr: 'Kurul son tarihi', en: 'Authority deadline' }, example: '2026-02-01T00:00:00.000Z', required: false },
    { key: 'dataSubjectDeadline', type: 'date', label: { tr: 'İlgili kişi son tarihi', en: 'Data-subject deadline' }, example: '2026-02-01T00:00:00.000Z', required: false },
    { key: 'hoursLeft', type: 'number', label: { tr: 'Kalan saat', en: 'Hours left' }, example: 24, required: false },
];
const BREACH_SLA_FIXTURE_FIELDS = {
    referenceNumber: 'BR-2026-0001', title: 'Veri olayı', authorityDeadline: '2026-02-01T00:00:00.000Z', dataSubjectDeadline: '2026-02-01T00:00:00.000Z', status: 'investigating',
};
const DISPOSAL_SLA_SAFE_FIELDS = [
    stringField('code', { tr: 'Kod', en: 'Code' }, { nullable: false }),
    stringField('title', { tr: 'Başlık', en: 'Title' }),
    stringField('status', { tr: 'Durum', en: 'Status' }),
    slaDateField('scheduledDate', { tr: 'Planlanan tarih', en: 'Scheduled date' }, {
        tr: 'İmha koşusunun planlanan tarihi.', en: 'Scheduled disposal date.',
    }),
];
const DISPOSAL_SLA_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'IMHA-2026-001', required: true, maxLength: 240 },
    { key: 'code', type: 'string', label: { tr: 'Kod', en: 'Code' }, example: 'IMHA-2026-001', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'Periyodik imha', required: false, maxLength: 240 },
    { key: 'scheduledDate', type: 'date', label: { tr: 'Planlanan tarih', en: 'Scheduled date' }, example: '2026-02-15T00:00:00.000Z', required: false },
    { key: 'expiredCount', type: 'number', label: { tr: 'Süresi dolan kayıt sayısı', en: 'Expired record count' }, example: 3, required: false },
];
const DISPOSAL_SLA_FIXTURE_FIELDS = {
    code: 'IMHA-2026-001', title: 'Periyodik imha', status: 'planned', scheduledDate: '2026-02-15T00:00:00.000Z',
};
const DPA_SLA_SAFE_FIELDS = [
    stringField('code', { tr: 'Kod', en: 'Code' }, { nullable: false }),
    stringField('title', { tr: 'Başlık', en: 'Title' }),
    stringField('status', { tr: 'Durum', en: 'Status' }),
    slaDateField('validUntil', { tr: 'Geçerlilik sonu', en: 'Valid until' }, {
        tr: 'DPA geçerlilik bitiş tarihi.', en: 'DPA validity end date.',
    }),
];
const DPA_SLA_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'DPA-2026-001', required: true, maxLength: 240 },
    { key: 'code', type: 'string', label: { tr: 'Kod', en: 'Code' }, example: 'DPA-2026-001', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'Veri işleme sözleşmesi', required: false, maxLength: 240 },
    { key: 'validUntil', type: 'date', label: { tr: 'Geçerlilik sonu', en: 'Valid until' }, example: '2026-03-01T00:00:00.000Z', required: false },
    { key: 'daysLeft', type: 'number', label: { tr: 'Kalan gün', en: 'Days left' }, example: 30, required: false },
];
const DPA_SLA_FIXTURE_FIELDS = {
    code: 'DPA-2026-001', title: 'Veri işleme sözleşmesi', status: 'active', validUntil: '2026-03-01T00:00:00.000Z',
};
const DPA_SUBPROCESSOR_SAFE_FIELDS = [
    stringField('code', { tr: 'Kod', en: 'Code' }, { nullable: false }),
    stringField('name', { tr: 'Ad', en: 'Name' }),
    stringField('subProcessorName', { tr: 'Alt işleyen', en: 'Sub-processor' }, { nullable: false }),
    stringField('decision', { tr: 'Karar', en: 'Decision' }),
];
const DPA_SUBPROCESSOR_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Ad', en: 'Name' }, example: 'DPA-2026-001', required: true, maxLength: 240 },
    { key: 'code', type: 'string', label: { tr: 'Kod', en: 'Code' }, example: 'DPA-2026-001', required: true, maxLength: 240 },
    { key: 'subProcessorName', type: 'string', label: { tr: 'Alt işleyen', en: 'Sub-processor' }, example: 'Cloud Vendor', required: true, maxLength: 240 },
    { key: 'decision', type: 'string', label: { tr: 'Karar', en: 'Decision' }, example: 'approved', required: false, maxLength: 64 },
];
const DPA_SUBPROCESSOR_FIXTURE_FIELDS = {
    code: 'DPA-2026-001', name: 'DPA-2026-001', subProcessorName: 'Cloud Vendor', decision: 'approved',
};
const BREACH_NOTIFIED_SAFE_FIELDS = [
    stringField('referenceNumber', { tr: 'Referans no', en: 'Reference number' }, { nullable: false }),
    stringField('name', { tr: 'Ad', en: 'Name' }),
];
const BREACH_NOTIFIED_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Ad', en: 'Name' }, example: 'BR-2026-0001', required: true, maxLength: 240 },
    { key: 'referenceNumber', type: 'string', label: { tr: 'Referans no', en: 'Reference number' }, example: 'BR-2026-0001', required: true, maxLength: 240 },
];
const BREACH_NOTIFIED_FIXTURE_FIELDS = {
    referenceNumber: 'BR-2026-0001', name: 'BR-2026-0001',
};
const SSC_SLA_SAFE_FIELDS = [
    stringField('code', { tr: 'Kod', en: 'Code' }),
    stringField('name', { tr: 'Ad', en: 'Name' }),
    stringField('status', { tr: 'Durum', en: 'Status' }),
    slaDateField('sscNotificationDeadline', { tr: 'Kurul son tarihi', en: 'Board notification deadline' }, {
        tr: 'SSC Kurul bildirim son tarihi.', en: 'SSC board notification deadline.',
    }),
];
const SSC_SLA_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Ad', en: 'Name' }, example: 'SSC-2026-001', required: true, maxLength: 240 },
    { key: 'code', type: 'string', label: { tr: 'Kod', en: 'Code' }, example: 'SSC-2026-001', required: false, maxLength: 240 },
    { key: 'sscNotificationDeadline', type: 'date', label: { tr: 'Kurul son tarihi', en: 'Board notification deadline' }, example: '2026-02-01T00:00:00.000Z', required: false },
    { key: 'daysLeft', type: 'number', label: { tr: 'Kalan gün', en: 'Days left' }, example: 1, required: false },
];
const SSC_SLA_FIXTURE_FIELDS = {
    code: 'SSC-2026-001', name: 'Standart sözleşme', status: 'active', sscNotificationDeadline: '2026-02-01T00:00:00.000Z',
};

const TICKET_PENDING_SAFE_FIELDS = [
    stringField('title', { tr: 'Başlık', en: 'Title' }),
    stringField('ticketNumber', { tr: 'Talep numarası', en: 'Ticket number' }),
];
const TICKET_PENDING_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Talep numarası', en: 'Ticket number' }, example: 'TCK-2026-001', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'Erişim talebi', required: false, maxLength: 240 },
    { key: 'ticketNumber', type: 'string', label: { tr: 'Talep numarası', en: 'Ticket number' }, example: 'TCK-2026-001', required: false, maxLength: 240 },
];
const TICKET_PENDING_FIXTURE_FIELDS = { title: 'Erişim talebi', ticketNumber: 'TCK-2026-001' };
const INCIDENT_PENDING_SAFE_FIELDS = [
    stringField('title', { tr: 'Başlık', en: 'Title' }),
    stringField('code', { tr: 'Olay kodu', en: 'Incident code' }),
];
const INCIDENT_PENDING_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Olay kodu', en: 'Incident code' }, example: 'INC-2026-001', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Başlık', en: 'Title' }, example: 'Şüpheli erişim', required: false, maxLength: 240 },
    { key: 'code', type: 'string', label: { tr: 'Olay kodu', en: 'Incident code' }, example: 'INC-2026-001', required: false, maxLength: 240 },
];
const INCIDENT_PENDING_FIXTURE_FIELDS = { title: 'Şüpheli erişim', code: 'INC-2026-001' };
const ASSET_PENDING_SAFE_FIELDS = [
    stringField('assetName', { tr: 'Varlık adı', en: 'Asset name' }),
    stringField('assetId', { tr: 'Envanter kodu', en: 'Inventory code' }),
];
const ASSET_PENDING_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Varlık adı', en: 'Asset name' }, example: 'Laptop-042', required: true, maxLength: 240 },
    { key: 'assetName', type: 'string', label: { tr: 'Varlık adı', en: 'Asset name' }, example: 'Laptop-042', required: false, maxLength: 240 },
    { key: 'assetId', type: 'string', label: { tr: 'Envanter kodu', en: 'Inventory code' }, example: 'AST-042', required: false, maxLength: 240 },
];
const ASSET_PENDING_FIXTURE_FIELDS = { assetName: 'Laptop-042', assetId: 'AST-042' };

function pendingOperationConfig({ name, label, resource, pageKey, adapter, source, safeFields, variables, fixtureFields, fixtureName, type, priority }) {
    const kind = type === 'success' ? 'importantState' : 'actionRequired';
    return {
        name,
        label,
        area: resource.type === 'Ticket' ? 'ticketOperations' : resource.type === 'Incident' ? 'incidentOperations' : 'assetInventory',
        kind,
        priority,
        module: resource.type === 'Ticket' ? 'ticketing' : resource.type === 'Incident' ? 'incident' : 'assetInventory',
        category: 'pendingOperation',
        resource,
        relation: 'requester',
        relationLabel: { tr: 'İşlemi isteyen kullanıcı', en: 'Requesting user' },
        adapter,
        guard: 'relation-only.v1',
        source,
        durable: true,
        safeFields,
        variables,
        fixtureFields,
        fixtureName,
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'requester' }], exclude: [] },
            content: { mode: 'eventDefault', type },
            channels: ['in-app'], deliveryClass: kind === 'actionRequired' ? 'actionable' : 'informational', priority, recipientCap: 1,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app'] },
    };
}

const PENDING_OPERATION_CONFIGS = Object.freeze([
    ...['pendingDeleteApproved', 'pendingDeleteRejected', 'pendingStatusRejected'].map((verb) => pendingOperationConfig({
        name: `ticket.${verb}`,
        label: { tr: verb === 'pendingDeleteApproved' ? 'Talep silme onayı' : verb === 'pendingDeleteRejected' ? 'Talep silme reddi' : 'Talep durum değişikliği reddi', en: `Ticket ${verb}` },
        resource: { type: 'Ticket', pageKey: 'ticketing' }, adapter: 'ticket.pending-operation.v1',
        source: 'apps/api/src/workflow/entity-lifecycle/ticket/pending-operation-executor.js:applyTicketPendingOperationFromInstance',
        safeFields: TICKET_PENDING_SAFE_FIELDS, variables: TICKET_PENDING_VARIABLES, fixtureFields: TICKET_PENDING_FIXTURE_FIELDS, fixtureName: 'TCK-2026-001',
        type: verb === 'pendingDeleteApproved' ? 'success' : 'warning', priority: verb === 'pendingDeleteApproved' ? 'normal' : 'high',
    })),
    ...['pendingDeleteApproved', 'pendingDeleteRejected', 'pendingStatusRejected'].map((verb) => pendingOperationConfig({
        name: `incident.${verb}`,
        label: { tr: verb === 'pendingDeleteApproved' ? 'Olay silme onayı' : verb === 'pendingDeleteRejected' ? 'Olay silme reddi' : 'Olay durum değişikliği reddi', en: `Incident ${verb}` },
        resource: { type: 'Incident', pageKey: 'incidentManagementIncidents' }, adapter: 'incident.pending-operation.v1',
        source: 'apps/api/src/workflow/entity-lifecycle/incident/pending-operation-executor.js:applyIncidentPendingOperationFromInstance',
        safeFields: INCIDENT_PENDING_SAFE_FIELDS, variables: INCIDENT_PENDING_VARIABLES, fixtureFields: INCIDENT_PENDING_FIXTURE_FIELDS, fixtureName: 'INC-2026-001',
        type: verb === 'pendingDeleteApproved' ? 'success' : 'warning', priority: verb === 'pendingDeleteApproved' ? 'normal' : 'high',
    })),
    ...['pendingDeleteApproved', 'pendingDeleteRejected'].map((verb) => pendingOperationConfig({
        name: `asset.${verb}`,
        label: { tr: verb === 'pendingDeleteApproved' ? 'Varlık silme onayı' : 'Varlık silme reddi', en: `Asset ${verb}` },
        resource: { type: 'Asset', pageKey: 'assetInventoryAssets' }, adapter: 'asset.pending-operation.v1',
        source: 'apps/api/src/workflow/entity-lifecycle/asset/pending-operation-executor.js:applyAssetPendingOperationFromInstance',
        safeFields: ASSET_PENDING_SAFE_FIELDS, variables: ASSET_PENDING_VARIABLES, fixtureFields: ASSET_PENDING_FIXTURE_FIELDS, fixtureName: 'Laptop-042',
        type: verb === 'pendingDeleteApproved' ? 'success' : 'warning', priority: verb === 'pendingDeleteApproved' ? 'normal' : 'high',
    })),
]);

const FORM_TASK_ASSIGNMENT_SAFE_FIELDS = [
    stringField('title', { tr: 'Görev başlığı', en: 'Task title' }),
];
const FORM_TASK_ASSIGNMENT_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Görev başlığı', en: 'Task title' }, example: 'Anket görevi', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Görev başlığı', en: 'Task title' }, example: 'Anket görevi', required: false, maxLength: 240 },
];
const FORM_TASK_ASSIGNMENT_FIXTURE_FIELDS = { title: 'Anket görevi' };

const COLLECTION_ASSIGNMENT_SAFE_FIELDS = [
    stringField('title', { tr: 'Görev başlığı', en: 'Task title' }),
    stringField('entityType', { tr: 'Kayıt tipi', en: 'Record type' }),
];
const COLLECTION_ASSIGNMENT_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Görev başlığı', en: 'Task title' }, example: 'Tedarikçi girişi', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Görev başlığı', en: 'Task title' }, example: 'Tedarikçi girişi', required: false, maxLength: 240 },
    { key: 'entityType', type: 'string', label: { tr: 'Kayıt tipi', en: 'Record type' }, example: 'Vendor', required: false, maxLength: 40 },
];
const COLLECTION_ASSIGNMENT_FIXTURE_FIELDS = { title: 'Tedarikçi girişi', entityType: 'Vendor' };

function collectionTaskConfig(name, label, source, extra = {}) {
    return {
        name,
        label,
        area: extra.area || 'workflow',
        kind: 'actionRequired',
        priority: extra.priority || 'high',
        module: 'workflow',
        category: 'tasks',
        resource: { type: 'CollectionAssignment', pageKey: 'myWork' },
        relation: 'assignedUser',
        relationLabel: { tr: 'Atanan kullanıcı', en: 'Assigned user' },
        adapter: 'collectionAssignment.assignment.v1',
        guard: 'relation-only.v1',
        source,
        durable: true,
        safeFields: COLLECTION_ASSIGNMENT_SAFE_FIELDS,
        variables: COLLECTION_ASSIGNMENT_VARIABLES,
        fixtureFields: COLLECTION_ASSIGNMENT_FIXTURE_FIELDS,
        fixtureName: 'Tedarikçi girişi',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'assignedUser' }], exclude: [] },
            content: { mode: 'eventDefault', type: extra.priority === 'urgent' ? 'error' : 'warning' },
            channels: ['in-app'],
            deliveryClass: 'actionable',
            priority: extra.priority || 'high',
            recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app'] },
    };
}

function collectionAssignmentNotificationConfigs() {
    const taskSource = 'apps/api/src/services/collection/collection-progress.js:refreshAssignmentContributions';
    const launchSource = 'packages/scheduled-jobs/src/collection-campaign-launch.js:processCollectionCampaignLaunches';
    const deadlineSource = 'packages/scheduled-jobs/src/collection-assignment-deadline-scanner.js:scanCollectionAssignmentDeadlines';
    const remindSource = 'apps/api/src/services/collection/collection-campaign.service.js:remindCollectionCampaign';
    const cancelSource = 'apps/api/src/services/collection/collection-campaign.service.js:cancelCollectionCampaign';
    return [
        collectionTaskConfig('collectionAssignment.assigned', { tr: 'Kayıt toplama görevi atandı', en: 'Collection task assigned' }, launchSource),
        collectionTaskConfig('collectionAssignment.completed', { tr: 'Kayıt toplama görevi tamamlandı', en: 'Collection task completed' }, taskSource),
        collectionTaskConfig('collectionAssignment.cancelled', { tr: 'Kayıt toplama görevi iptal edildi', en: 'Collection task cancelled' }, cancelSource),
        collectionTaskConfig('collectionAssignment.reopened', { tr: 'Kayıt toplama görevi yeniden açıldı', en: 'Collection task reopened' }, taskSource),
        collectionTaskConfig('collectionAssignment.reminded', { tr: 'Kayıt toplama hatırlatması', en: 'Collection task reminder' }, remindSource),
        collectionTaskConfig('collectionAssignment.deadlineWarning', { tr: 'Kayıt toplama süre uyarısı', en: 'Collection task deadline warning' }, deadlineSource, { area: 'sla' }),
        collectionTaskConfig('collectionAssignment.overdue', { tr: 'Kayıt toplama süresi aşıldı', en: 'Collection task overdue' }, deadlineSource, { area: 'sla', priority: 'urgent' }),
    ];
}

function collectionCampaignNotificationConfigs() {
    const base = {
        area: 'workflow',
        kind: 'actionRequired',
        priority: 'normal',
        module: 'workflow',
        category: 'tasks',
        resource: { type: 'CollectionCampaign', pageKey: 'collectionCampaigns' },
        relation: 'createdBy',
        relationLabel: { tr: 'Kampanyayı başlatan', en: 'Campaign owner' },
        adapter: 'collectionCampaign.lifecycle.v1',
        guard: 'relation-only.v1',
        durable: true,
        safeFields: [stringField('name', { tr: 'Kampanya adı', en: 'Campaign name' })],
        variables: [
            { key: 'name', type: 'string', label: { tr: 'Kampanya adı', en: 'Campaign name' }, example: 'Tedarikçi girişi', required: true, maxLength: 240 },
        ],
        fixtureFields: { name: 'Tedarikçi girişi' },
        fixtureName: 'Tedarikçi girişi',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'createdBy' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'info' },
            channels: ['in-app'],
            deliveryClass: 'actionable',
            priority: 'normal',
            recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app'] },
    };
    return [
        {
            ...base,
            name: 'collectionCampaign.launched',
            label: { tr: 'Kayıt toplama kampanyası başladı', en: 'Collection campaign launched' },
            source: 'packages/scheduled-jobs/src/collection-campaign-launch.js:processCollectionCampaignLaunches',
        },
        {
            ...base,
            name: 'collectionCampaign.cancelled',
            label: { tr: 'Kayıt toplama kampanyası iptal edildi', en: 'Collection campaign cancelled' },
            source: 'apps/api/src/services/collection/collection-campaign.service.js:cancelCollectionCampaign',
        },
    ];
}

const OPERATIONAL_RUN_SAFE_FIELDS = [
    stringField('code', { tr: 'Koşu kodu', en: 'Run code' }, { nullable: false }),
    stringField('sourceTitle', { tr: 'Kaynak başlığı', en: 'Source title' }, { nullable: false }),
    stringField('sourceType', { tr: 'Kaynak türü', en: 'Source type' }, { nullable: false }),
    slaDateField('startedAt', { tr: 'Başlangıç zamanı', en: 'Start time' }, {
        tr: 'Operasyon koşusunun başlangıç zamanı.', en: 'Operational run start time.',
    }),
];
const OPERATIONAL_RUN_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Kaynak başlığı', en: 'Source title' }, example: 'Kriz müdahale operasyonu', required: true, maxLength: 240 },
    { key: 'code', type: 'string', label: { tr: 'Koşu kodu', en: 'Run code' }, example: 'OR-2026-ABC12345', required: true, maxLength: 240 },
    { key: 'sourceTitle', type: 'string', label: { tr: 'Kaynak başlığı', en: 'Source title' }, example: 'Kriz müdahale operasyonu', required: true, maxLength: 240 },
    { key: 'taskCount', type: 'number', label: { tr: 'Görev sayısı', en: 'Task count' }, example: 4, required: false },
    { key: 'type', type: 'string', label: { tr: 'Güncelleme türü', en: 'Update type' }, example: 'progress', required: false, maxLength: 40 },
    { key: 'reason', type: 'string', label: { tr: 'Gerekçe', en: 'Reason' }, example: 'Duraklatıldı', required: false, maxLength: 240 },
    { key: 'summary', type: 'string', label: { tr: 'Özet', en: 'Summary' }, example: 'Koşu tamamlandı', required: false, maxLength: 240 },
];
const OPERATIONAL_TASK_SAFE_FIELDS = [
    stringField('title', { tr: 'Görev başlığı', en: 'Task title' }, { nullable: false }),
    slaDateField('dueAt', { tr: 'Son tarih', en: 'Due date' }, {
        tr: 'Operasyon görevinin tamamlanma son tarihi.', en: 'Operational task completion deadline.',
    }),
    stringField('status', { tr: 'Durum', en: 'Status' }, { nullable: false }),
];
const OPERATIONAL_TASK_VARIABLES = [
    { key: 'name', type: 'string', label: { tr: 'Görev başlığı', en: 'Task title' }, example: 'Kriz iletişim planını etkinleştir', required: true, maxLength: 240 },
    { key: 'title', type: 'string', label: { tr: 'Görev başlığı', en: 'Task title' }, example: 'Kriz iletişim planını etkinleştir', required: true, maxLength: 240 },
    { key: 'runCode', type: 'string', label: { tr: 'Koşu kodu', en: 'Run code' }, example: 'OR-2026-ABC12345', required: true, maxLength: 240 },
    { key: 'sourceTitle', type: 'string', label: { tr: 'Kaynak başlığı', en: 'Source title' }, example: 'Kriz müdahale operasyonu', required: true, maxLength: 240 },
    { key: 'dueAt', type: 'date', label: { tr: 'Son tarih', en: 'Due date' }, example: '2026-08-28T15:00:00.000Z', required: true },
];
const OPERATIONAL_RUN_FIXTURE_FIELDS = {
    code: 'OR-2026-ABC12345', sourceTitle: 'Kriz müdahale operasyonu', sourceType: 'CrisisEvent', startedAt: '2026-08-28T12:00:00.000Z',
};
const OPERATIONAL_TASK_FIXTURE_FIELDS = {
    title: 'Kriz iletişim planını etkinleştir', dueAt: '2026-08-28T15:00:00.000Z', status: 'ready',
};

const OPERATIONAL_RUN_CHANNEL_POLICY = { required: ['in-app'], allowed: ['in-app', 'email', 'push'] };
const OPERATIONAL_RUN_LIFECYCLE_RESOURCE = { type: 'OperationalRun', pageKey: 'myWork' };
const OPERATIONAL_TASK_RESOURCE = { type: 'OperationalRunTask', pageKey: 'myWork' };
const OPERATIONAL_RR_RELATION_LABEL = { tr: 'R&R ekip üyeleri', en: 'R&R team members' };

function operationalRunLifecycleConfig(name, label, source, extra = {}) {
    return {
        name,
        label,
        area: 'bcmOperations',
        kind: extra.kind || 'importantState',
        priority: extra.priority || 'high',
        module: 'bcm',
        category: 'operationalExecution',
        resource: OPERATIONAL_RUN_LIFECYCLE_RESOURCE,
        relation: 'participants',
        relationLabel: OPERATIONAL_RR_RELATION_LABEL,
        adapter: 'operationalRun.lifecycle.v1',
        guard: 'relation-only.v1',
        source,
        durable: true,
        safeFields: OPERATIONAL_RUN_SAFE_FIELDS,
        variables: OPERATIONAL_RUN_VARIABLES,
        fixtureFields: OPERATIONAL_RUN_FIXTURE_FIELDS,
        fixtureName: 'Kriz müdahale operasyonu',
        channelPolicy: OPERATIONAL_RUN_CHANNEL_POLICY,
        ...extra.policy,
    };
}

function operationalRunTaskConfig(name, label, source, extra = {}) {
    return {
        name,
        label,
        area: extra.area || 'bcmOperations',
        kind: extra.kind || 'importantState',
        priority: extra.priority || 'high',
        module: 'bcm',
        category: 'operationalExecution',
        resource: OPERATIONAL_TASK_RESOURCE,
        relation: extra.relation || 'recipients',
        relationLabel: extra.relationLabel || OPERATIONAL_RR_RELATION_LABEL,
        adapter: 'operationalRun.task.v1',
        guard: 'relation-only.v1',
        source,
        durable: true,
        safeFields: OPERATIONAL_TASK_SAFE_FIELDS,
        variables: OPERATIONAL_TASK_VARIABLES,
        fixtureFields: OPERATIONAL_TASK_FIXTURE_FIELDS,
        fixtureName: 'Kriz iletişim planını etkinleştir',
        channelPolicy: OPERATIONAL_RUN_CHANNEL_POLICY,
    };
}

const MANAGED_CONFIGS = Object.freeze([
    ...PENDING_OPERATION_CONFIGS,
    operationalRunLifecycleConfig(
        'operationalRun.started',
        { tr: 'Operasyon koşusu başladı', en: 'Operational run started' },
        'apps/api/src/services/bcm/operational-run.service.js:startOperationalRun',
        {
            policy: {
                defaultPolicy: {
                    audience: { include: [{ type: 'eventRelation', relation: 'participants' }], exclude: [] },
                    content: {
                        mode: 'eventDefault',
                        type: 'info',
                    },
                    channels: ['in-app'],
                    deliveryClass: 'informational',
                    priority: 'high',
                    recipientCap: 500,
                },
            },
        },
    ),
    operationalRunLifecycleConfig(
        'operationalRun.paused',
        { tr: 'Operasyon koşusu duraklatıldı', en: 'Operational run paused' },
        'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
    ),
    operationalRunLifecycleConfig(
        'operationalRun.resumed',
        { tr: 'Operasyon koşusu sürdürüldü', en: 'Operational run resumed' },
        'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
    ),
    operationalRunLifecycleConfig(
        'operationalRun.completed',
        { tr: 'Operasyon koşusu tamamlandı', en: 'Operational run completed' },
        'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
    ),
    operationalRunLifecycleConfig(
        'operationalRun.cancelled',
        { tr: 'Operasyon koşusu iptal edildi', en: 'Operational run cancelled' },
        'apps/api/src/services/bcm/operational-run.service.js:transitionRun',
    ),
    operationalRunLifecycleConfig(
        'operationalRun.update.appended',
        { tr: 'Operasyon güncellemesi eklendi', en: 'Operational run update appended' },
        'apps/api/src/services/bcm/operational-run.service.js:appendOperationalRunUpdate',
    ),
    operationalRunTaskConfig(
        'operationalRun.task.assigned',
        { tr: 'Operasyon görevi atandı', en: 'Operational task assigned' },
        'apps/api/src/services/bcm/operational-run.service.js:startOperationalRun',
        {
            kind: 'actionRequired',
            relation: 'assignedUser',
            relationLabel: OPERATIONAL_RR_RELATION_LABEL,
        },
    ),
    operationalRunTaskConfig(
        'operationalRun.task.started',
        { tr: 'Operasyon görevi başlatıldı', en: 'Operational task started' },
        'apps/api/src/services/bcm/operational-run.service.js:startOwnOperationalTask',
    ),
    operationalRunTaskConfig(
        'operationalRun.task.completed',
        { tr: 'Operasyon görevi tamamlandı', en: 'Operational task completed' },
        'apps/api/src/services/bcm/operational-run.service.js:completeOwnOperationalTask',
    ),
    operationalRunTaskConfig(
        'operationalRun.task.cancelled',
        { tr: 'Operasyon görevi iptal edildi', en: 'Operational task cancelled' },
        'apps/api/src/services/bcm/operational-run.service.js:cancelUnfinishedTasks',
    ),
    operationalRunTaskConfig(
        'operationalRun.task.reassigned',
        { tr: 'Operasyon görevi yeniden atandı', en: 'Operational task reassigned' },
        'apps/api/src/services/bcm/operational-run.service.js:reassignOperationalTask',
        { kind: 'actionRequired' },
    ),
    operationalRunTaskConfig(
        'operationalRun.task.evidenceUploaded',
        { tr: 'Operasyon görevi kanıtı yüklendi', en: 'Operational task evidence uploaded' },
        'apps/api/src/services/bcm/operational-run-evidence.service.js:uploadOperationalTaskEvidence',
    ),
    operationalRunTaskConfig(
        'operationalRun.task.dependenciesOverridden',
        { tr: 'Operasyon görevi bağımlılığı geçersiz kılındı', en: 'Operational task dependencies overridden' },
        'apps/api/src/services/bcm/operational-run.service.js:overrideOperationalTaskDependencies',
    ),
    operationalRunTaskConfig(
        'operationalRun.task.deadlineWarning',
        { tr: 'Operasyon görevi süre uyarısı', en: 'Operational task deadline warning' },
        'packages/scheduled-jobs/src/operational-run-deadline-scanner.js:scanOperationalRunDeadlines',
        {
            area: 'sla',
            kind: 'actionRequired',
            relation: 'assignedUser',
            relationLabel: OPERATIONAL_RR_RELATION_LABEL,
        },
    ),
    operationalRunTaskConfig(
        'operationalRun.task.overdue',
        { tr: 'Operasyon görevinin süresi aşıldı', en: 'Operational task overdue' },
        'packages/scheduled-jobs/src/operational-run-deadline-scanner.js:scanOperationalRunDeadlines',
        {
            area: 'sla',
            kind: 'actionRequired',
            priority: 'urgent',
            relation: 'recipients',
            relationLabel: OPERATIONAL_RR_RELATION_LABEL,
        },
    ),
    {
        name: 'formTask.assigned', label: { tr: 'Anket görevi atandı', en: 'Form task assigned' },
        area: 'workflow', kind: 'actionRequired', priority: 'high', module: 'workflow', category: 'tasks',
        resource: { type: 'FormTask', pageKey: 'myWork' }, relation: 'assignedUser',
        relationLabel: { tr: 'Atanan kullanıcı', en: 'Assigned user' }, adapter: 'formTask.assignment.v1',
        guard: 'relation-only.v1', source: 'packages/scheduled-jobs/src/survey-dispatch-execute.js:executeSurveyDispatch;apps/api/src/controllers/form-task.controller.js:assign', durable: true,
        safeFields: FORM_TASK_ASSIGNMENT_SAFE_FIELDS, variables: FORM_TASK_ASSIGNMENT_VARIABLES, fixtureFields: FORM_TASK_ASSIGNMENT_FIXTURE_FIELDS, fixtureName: 'Anket görevi',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'assignedUser' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app'] },
    },
    ...collectionAssignmentNotificationConfigs(),
    ...collectionCampaignNotificationConfigs(),
    {
        name: 'dataBreach.deadlineReminder', label: { tr: 'Veri ihlali süre hatırlatması', en: 'Data breach deadline reminder' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'sla',
        resource: { type: 'DataBreach', pageKey: 'gdprDataBreach' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataBreach.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachNotificationSla', durable: true,
        safeFields: BREACH_SLA_SAFE_FIELDS, variables: BREACH_SLA_VARIABLES, fixtureFields: BREACH_SLA_FIXTURE_FIELDS, fixtureName: 'BR-2026-0001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
    },
    {
        name: 'dataBreach.deadlineOverdue', label: { tr: 'Veri ihlali süre aşımı', en: 'Data breach deadline overdue' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'privacy', category: 'sla',
        resource: { type: 'DataBreach', pageKey: 'gdprDataBreach' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataBreach.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachNotificationSla', durable: true,
        safeFields: BREACH_SLA_SAFE_FIELDS, variables: BREACH_SLA_VARIABLES, fixtureFields: BREACH_SLA_FIXTURE_FIELDS, fixtureName: 'BR-2026-0001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'error' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'urgent', recipientCap: 500,
        },
    },
    {
        name: 'dataBreach.dataSubjectDeadlineReminder', label: { tr: 'İlgili kişi bildirim süre hatırlatması', en: 'Data-subject deadline reminder' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'sla',
        resource: { type: 'DataBreach', pageKey: 'gdprDataBreach' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataBreach.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachDataSubjectNotificationSla', durable: true,
        safeFields: BREACH_SLA_SAFE_FIELDS, variables: BREACH_SLA_VARIABLES, fixtureFields: BREACH_SLA_FIXTURE_FIELDS, fixtureName: 'BR-2026-0001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
    },
    {
        name: 'dataBreach.dataSubjectDeadlineOverdue', label: { tr: 'İlgili kişi bildirim süre aşımı', en: 'Data-subject deadline overdue' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'privacy', category: 'sla',
        resource: { type: 'DataBreach', pageKey: 'gdprDataBreach' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataBreach.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/breach-notification-scanner.js:scanBreachDataSubjectNotificationSla', durable: true,
        safeFields: BREACH_SLA_SAFE_FIELDS, variables: BREACH_SLA_VARIABLES, fixtureFields: BREACH_SLA_FIXTURE_FIELDS, fixtureName: 'BR-2026-0001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'error' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'urgent', recipientCap: 500,
        },
    },
    {
        name: 'disposalRun.periodicDue', label: { tr: 'Periyodik imha dönemi', en: 'Periodic disposal due' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'disposal',
        resource: { type: 'DisposalRun', pageKey: 'gdprDisposal' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'disposalRun.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/disposal-scanner.js:scanRetentionDisposal', durable: true,
        safeFields: DISPOSAL_SLA_SAFE_FIELDS, variables: DISPOSAL_SLA_VARIABLES, fixtureFields: DISPOSAL_SLA_FIXTURE_FIELDS, fixtureName: 'IMHA-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
    },
    {
        name: 'disposalRun.recordsExpired', label: { tr: 'Saklama süresi dolan kayıtlar', en: 'Expired records found' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'disposal',
        resource: { type: 'DisposalRun', pageKey: 'gdprDisposal' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'disposalRun.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/disposal-scanner.js:scanRetentionDisposal', durable: true,
        safeFields: DISPOSAL_SLA_SAFE_FIELDS, variables: DISPOSAL_SLA_VARIABLES, fixtureFields: DISPOSAL_SLA_FIXTURE_FIELDS, fixtureName: 'IMHA-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
    },
    {
        name: 'dataProcessingAgreement.deadlineReminder', label: { tr: 'DPA süre hatırlatması', en: 'DPA expiry reminder' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'dpa',
        resource: { type: 'DataProcessingAgreement', pageKey: 'gdprDpa' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataProcessingAgreement.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/dpa-expiry-scanner.js:scanDpaExpiry', durable: true,
        safeFields: DPA_SLA_SAFE_FIELDS, variables: DPA_SLA_VARIABLES, fixtureFields: DPA_SLA_FIXTURE_FIELDS, fixtureName: 'DPA-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
    },
    {
        name: 'dataProcessingAgreement.expired', label: { tr: 'DPA süresi doldu', en: 'DPA expired' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'privacy', category: 'dpa',
        resource: { type: 'DataProcessingAgreement', pageKey: 'gdprDpa' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataProcessingAgreement.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/dpa-expiry-scanner.js:scanDpaExpiry', durable: true,
        safeFields: DPA_SLA_SAFE_FIELDS, variables: DPA_SLA_VARIABLES, fixtureFields: DPA_SLA_FIXTURE_FIELDS, fixtureName: 'DPA-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'error' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'urgent', recipientCap: 500,
        },
    },
    {
        name: 'dataProcessingAgreement.subProcessorAdded',
        label: { tr: 'Alt işleyen eklendi', en: 'Sub-processor added' },
        area: 'privacyCatalog', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'dpa',
        resource: { type: 'DataProcessingAgreement', pageKey: 'gdprDpa' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' },
        adapter: 'crud.dataProcessingAgreement.v1',
        guard: 'page-view.v1',
        source: 'apps/api/src/controllers/data-processing-agreement.controller.js:addSubProcessor',
        durable: true,
        safeFields: DPA_SUBPROCESSOR_SAFE_FIELDS,
        variables: DPA_SUBPROCESSOR_VARIABLES,
        fixtureFields: DPA_SUBPROCESSOR_FIXTURE_FIELDS,
        fixtureName: 'DPA-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'pageViewers' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email', 'sms', 'push'] },
        audienceSelectors: MANAGED_CRUD_AUDIENCE_SELECTORS,
    },
    {
        name: 'dataProcessingAgreement.subProcessorDecided',
        label: { tr: 'Alt işleyen kararı', en: 'Sub-processor decided' },
        area: 'privacyCatalog', kind: 'importantState', priority: 'normal', module: 'privacy', category: 'dpa',
        resource: { type: 'DataProcessingAgreement', pageKey: 'gdprDpa' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' },
        adapter: 'crud.dataProcessingAgreement.v1',
        guard: 'page-view.v1',
        source: 'apps/api/src/controllers/data-processing-agreement.controller.js:decideSubProcessor',
        durable: true,
        safeFields: DPA_SUBPROCESSOR_SAFE_FIELDS,
        variables: DPA_SUBPROCESSOR_VARIABLES,
        fixtureFields: DPA_SUBPROCESSOR_FIXTURE_FIELDS,
        fixtureName: 'DPA-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'pageViewers' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'info' },
            channels: ['in-app'], deliveryClass: 'informational', priority: 'normal', recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email', 'sms', 'push'] },
        audienceSelectors: MANAGED_CRUD_AUDIENCE_SELECTORS,
    },
    {
        name: 'gdpr.dataBreach.notified',
        label: { tr: 'Veri ihlali Kurula bildirildi', en: 'Authority notified of data breach' },
        area: 'privacyCatalog', kind: 'importantState', priority: 'high', module: 'privacy', category: 'dataBreach',
        resource: { type: 'DataBreach', pageKey: 'gdprDataBreach' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' },
        adapter: 'crud.dataBreach.v1',
        guard: 'page-view.v1',
        source: 'apps/api/src/controllers/data-breach.controller.js:update',
        durable: true,
        safeFields: BREACH_NOTIFIED_SAFE_FIELDS,
        variables: BREACH_NOTIFIED_VARIABLES,
        fixtureFields: BREACH_NOTIFIED_FIXTURE_FIELDS,
        fixtureName: 'BR-2026-0001',
        defaultPolicy: {
            audience: { include: [{ type: 'pageViewers' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'success' },
            channels: ['in-app'], deliveryClass: 'informational', priority: 'high', recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email', 'sms', 'push'] },
        audienceSelectors: MANAGED_CRUD_AUDIENCE_SELECTORS,
    },
    {
        name: 'internationalTransfer.sscDeadlineReminder', label: { tr: 'SSC Kurul bildirim hatırlatması', en: 'SSC board notification reminder' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'internationalTransfer',
        resource: { type: 'InternationalTransfer', pageKey: 'definitionsKvkkInternationalTransfer' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'internationalTransfer.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/ssc-notification-scanner.js:scanSscNotificationSla', durable: true,
        safeFields: SSC_SLA_SAFE_FIELDS, variables: SSC_SLA_VARIABLES, fixtureFields: SSC_SLA_FIXTURE_FIELDS, fixtureName: 'SSC-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
    },
    {
        name: 'internationalTransfer.sscDeadlineOverdue', label: { tr: 'SSC Kurul bildirim süresi aşıldı', en: 'SSC board notification overdue' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'privacy', category: 'internationalTransfer',
        resource: { type: 'InternationalTransfer', pageKey: 'definitionsKvkkInternationalTransfer' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'internationalTransfer.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/ssc-notification-scanner.js:scanSscNotificationSla', durable: true,
        safeFields: SSC_SLA_SAFE_FIELDS, variables: SSC_SLA_VARIABLES, fixtureFields: SSC_SLA_FIXTURE_FIELDS, fixtureName: 'SSC-2026-001',
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'error' },
            channels: ['in-app'], deliveryClass: 'actionable', priority: 'urgent', recipientCap: 500,
        },
    },
    {
        name: 'dataSubjectRequest.deadlineReminder', label: { tr: 'DSR süre hatırlatması', en: 'DSR deadline reminder' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'privacy', category: 'sla',
        resource: { type: 'DataSubjectRequest', pageKey: 'gdprDataSubjectRequest' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataSubjectRequest.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/dsr-sla-scanner.js:scanDsrSla', durable: true,
        safeFields: DSR_SLA_SAFE_FIELDS, variables: DSR_SLA_VARIABLES, fixtureFields: DSR_SLA_FIXTURE_FIELDS,
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'warning' },
            channels: ['in-app', 'email'], deliveryClass: 'actionable', priority: 'high', recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email'] },
    },
    {
        name: 'dataSubjectRequest.deadlineOverdue', label: { tr: 'DSR süre aşımı', en: 'DSR deadline overdue' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'privacy', category: 'sla',
        resource: { type: 'DataSubjectRequest', pageKey: 'gdprDataSubjectRequest' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'dataSubjectRequest.sla.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/dsr-sla-scanner.js:scanDsrSla', durable: true,
        safeFields: DSR_SLA_SAFE_FIELDS, variables: DSR_SLA_VARIABLES, fixtureFields: DSR_SLA_FIXTURE_FIELDS,
        defaultPolicy: {
            audience: { include: [{ type: 'eventRelation', relation: 'recipients' }], exclude: [] },
            content: { mode: 'eventDefault', type: 'error' },
            channels: ['in-app', 'email'], deliveryClass: 'actionable', priority: 'urgent', recipientCap: 500,
        },
        channelPolicy: { required: ['in-app'], allowed: ['in-app', 'email'] },
    },
    ...SPECIALTY_DECISION_CONFIGS,
    {
        name: 'workflow.task.assigned', label: { tr: 'İş akışı görevi atandı', en: 'Workflow task assigned' },
        area: 'workflow', kind: 'actionRequired', priority: 'high', module: 'workflow', category: 'tasks',
        resource: { type: 'WorkflowTask', pageKey: 'myWork' }, relation: 'assignedUser',
        relationLabel: { tr: 'Atanan kullanıcı', en: 'Assigned user' }, adapter: 'workflow.task-assignment.v1',
        guard: 'workflow-task-access.v1', source: 'packages/workflow-engine/src/bridges/task-assigned-emit.js', durable: true,
    },
    {
        name: 'workflow.task.slaWarning', label: { tr: 'İş akışı SLA uyarısı', en: 'Workflow task SLA warning' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'workflow', category: 'sla',
        resource: { type: 'WorkflowTask', pageKey: 'myWork' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'workflow.task-sla.v1',
        guard: 'workflow-task-access.v1', source: 'packages/scheduled-jobs/src/workflow-task-sla.js', durable: true,
    },
    {
        name: 'workflow.task.slaBreached', label: { tr: 'İş akışı SLA ihlali', en: 'Workflow task SLA breach' },
        area: 'sla', kind: 'actionRequired', priority: 'urgent', module: 'workflow', category: 'sla',
        resource: { type: 'WorkflowTask', pageKey: 'myWork' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'workflow.task-sla.v1',
        guard: 'workflow-task-access.v1', source: 'packages/scheduled-jobs/src/workflow-task-sla.js', durable: true,
    },
    {
        name: 'workflow.task.slaEscalated', label: { tr: 'İş akışı SLA eskalasyonu', en: 'Workflow task SLA escalation' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'workflow', category: 'sla',
        resource: { type: 'WorkflowTask', pageKey: 'myWork' }, relation: 'recipients',
        relationLabel: { tr: 'Bildirim alıcıları', en: 'Notification recipients' }, adapter: 'workflow.task-sla.v1',
        guard: 'workflow-task-access.v1', source: 'packages/scheduled-jobs/src/workflow-task-sla.js', durable: true,
    },
    {
        name: 'workflow.informed.assigned', label: { tr: 'Bilgilendirme atandı', en: 'Workflow information assigned' },
        area: 'workflow', kind: 'importantState', priority: 'normal', module: 'workflow', category: 'informed',
        resource: { type: 'WorkflowInformedReceipt', pageKey: 'myWork' }, relation: 'informedUser',
        relationLabel: { tr: 'Bilgilendirilen kullanıcı', en: 'Informed user' }, adapter: 'workflow.informed-assignment.v1',
        guard: 'workflow-informed-access.v1', source: 'packages/workflow-engine/src/bridges/informed-assigned-emit.js', durable: true,
    },
    {
        name: 'role.assigned', label: { tr: 'Rol atandı', en: 'Role assigned' },
        area: 'securityAccess', kind: 'actionRequired', priority: 'high', module: 'access', category: 'roles',
        resource: { type: 'Role', pageKey: 'userOperationsRoleManagement' }, relation: 'affectedUsers',
        relationLabel: { tr: 'Etkilenen kullanıcılar', en: 'Affected users' }, adapter: 'role.access-assignment.v1',
        guard: 'role-access.v1', source: 'apps/api/src/controllers/role.controller.js', durable: true,
    },
    {
        name: 'role.permissionsChanged', label: { tr: 'Rol izinleri değişti', en: 'Role permissions changed' },
        area: 'securityAccess', kind: 'importantState', priority: 'high', module: 'access', category: 'roles',
        resource: { type: 'Role', pageKey: 'userOperationsRoleManagement' }, relation: 'affectedUsers',
        relationLabel: { tr: 'Etkilenen kullanıcılar', en: 'Affected users' }, adapter: 'role.access-permissions.v1',
        guard: 'role-access.v1', source: 'apps/api/src/controllers/role.controller.js', durable: true,
    },
    ...['assigned', 'resolved', 'closed'].map((verb) => ({
        name: `ticket.${verb}`,
        label: {
            tr: verb === 'assigned' ? 'Talep atandı' : verb === 'resolved' ? 'Talep çözüldü' : 'Talep kapatıldı',
            en: `Ticket ${verb}`,
        },
        area: 'ticketOperations', kind: verb === 'assigned' ? 'actionRequired' : 'importantState',
        priority: verb === 'assigned' ? 'high' : 'normal', module: 'ticketing', category: 'ticketLifecycle',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: verb === 'assigned' ? 'assignees' : 'stakeholders',
        relationLabel: verb === 'assigned'
            ? { tr: 'Atananlar', en: 'Assignees' }
            : { tr: 'İlgili taraflar', en: 'Stakeholders' },
        adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: verb === 'assigned'
            ? 'apps/api/src/controllers/ticket.controller.js;apps/api/src/workflow/entity-lifecycle/ticket/lifecycle.service.js;apps/api/src/workflow/entity-lifecycle/ticket/auto-assign-on-approve.js'
            : 'apps/api/src/controllers/ticket.controller.js;apps/api/src/workflow/entity-lifecycle/ticket/lifecycle.service.js;apps/api/src/workflow/entity-lifecycle/ticket/lifecycle-operation-executors.js',
        durable: true,
    })),
    {
        name: 'ticket.submitted', label: { tr: 'Talep onaya gönderildi', en: 'Ticket submitted' },
        area: 'ticketOperations', kind: 'actionRequired', priority: 'high', module: 'ticketing', category: 'ticketLifecycle',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: 'approvers',
        relationLabel: { tr: 'Onaylayanlar', en: 'Approvers' }, adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: 'apps/api/src/controllers/ticket.controller.js', durable: true,
    },
    {
        name: 'ticket.cancelled', label: { tr: 'Talep iptal edildi', en: 'Ticket cancelled' },
        area: 'ticketOperations', kind: 'importantState', priority: 'normal', module: 'ticketing', category: 'ticketLifecycle',
        resource: { type: 'Ticket', pageKey: 'ticketing' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'ticket.lifecycle.v1', guard: 'ticket-access.v1',
        source: 'apps/api/src/controllers/ticket.controller.js;apps/api/src/workflow/entity-lifecycle/ticket/lifecycle.service.js', durable: true,
    },
    {
        name: 'incident.submitted', label: { tr: 'Olay onaya gönderildi', en: 'Incident submitted' },
        area: 'incidentOperations', kind: 'actionRequired', priority: 'high', module: 'incident', category: 'incidentLifecycle',
        resource: { type: 'Incident', pageKey: 'incidentManagementIncidents' }, relation: 'approvers',
        relationLabel: { tr: 'Onaylayanlar', en: 'Approvers' }, adapter: 'incident.lifecycle.v1', guard: 'incident-access.v1',
        source: 'apps/api/src/controllers/incident-lifecycle.controller.js', durable: true,
    },
    {
        name: 'incident.cancelled', label: { tr: 'Olay iptal edildi', en: 'Incident cancelled' },
        area: 'incidentOperations', kind: 'importantState', priority: 'normal', module: 'incident', category: 'incidentLifecycle',
        resource: { type: 'Incident', pageKey: 'incidentManagementIncidents' }, relation: 'stakeholders',
        relationLabel: { tr: 'İlgili taraflar', en: 'Stakeholders' }, adapter: 'incident.lifecycle.v1', guard: 'incident-access.v1',
        source: 'apps/api/src/workflow/entity-lifecycle/incident/lifecycle.service.js', durable: true,
    },
    ...['assigned', 'resolved', 'closed', 'slaBreached'].map((verb) => ({
        name: `incident.${verb}`,
        label: {
            tr: verb === 'assigned' ? 'Olay atandı' : verb === 'resolved' ? 'Olay çözüldü' : verb === 'closed' ? 'Olay kapatıldı' : 'Olay SLA süresini aştı',
            en: verb === 'slaBreached' ? 'Incident SLA breached' : `Incident ${verb}`,
        },
        area: verb === 'slaBreached' ? 'sla' : 'incidentOperations',
        kind: verb === 'assigned' || verb === 'slaBreached' ? 'actionRequired' : 'importantState',
        priority: verb === 'slaBreached' ? 'urgent' : verb === 'assigned' ? 'high' : 'normal',
        module: 'incident', category: verb === 'slaBreached' ? 'sla' : 'incidentLifecycle',
        resource: { type: 'Incident', pageKey: 'incidentManagementIncidents' },
        relation: verb === 'assigned' ? 'assignees' : 'stakeholders',
        relationLabel: verb === 'assigned'
            ? { tr: 'Atananlar', en: 'Assignees' }
            : { tr: 'İlgili taraflar', en: 'Stakeholders' },
        adapter: 'incident.lifecycle.v1', guard: 'incident-access.v1',
        source: verb === 'slaBreached'
            ? 'apps/api/src/controllers/incident.controller.js:update + apps/api/src/lib/incident/entity-update-draft.service.js:applyIncidentUpdateDraft + packages/scheduled-jobs/src/incident-sla-breach.js'
            : verb === 'assigned'
                ? 'apps/api/src/controllers/incident-lifecycle.controller.js;apps/api/src/workflow/entity-lifecycle/incident/lifecycle.service.js;apps/api/src/workflow/entity-lifecycle/incident/auto-assign-on-approve.js'
                : 'apps/api/src/controllers/incident-lifecycle.controller.js;apps/api/src/workflow/entity-lifecycle/incident/lifecycle.service.js;apps/api/src/workflow/entity-lifecycle/incident/lifecycle-operation-executors.js',
        durable: true,
    })),
    {
        name: 'training.assigned', label: { tr: 'Eğitim atandı', en: 'Training assigned' },
        area: 'generalCrud', kind: 'actionRequired', priority: 'high', module: 'training', category: 'trainingLifecycle',
        resource: { type: 'Training', pageKey: 'isoTraining' }, relation: 'assignedUser',
        relationLabel: { tr: 'Atanan kullanıcı', en: 'Assigned user' }, adapter: 'training.lifecycle.v1',
        guard: 'page-view.v1', source: 'apps/api/src/lib/training/training-lifecycle-events.js:emitTrainingAssigned', durable: true,
    },
    {
        name: 'training.completed', label: { tr: 'Eğitim tamamlandı', en: 'Training completed' },
        area: 'generalCrud', kind: 'importantState', priority: 'normal', module: 'training', category: 'trainingLifecycle',
        resource: { type: 'Training', pageKey: 'isoTraining' }, relation: 'assignedUser',
        relationLabel: { tr: 'Atanan kullanıcı', en: 'Assigned user' }, adapter: 'training.lifecycle.v1',
        guard: 'page-view.v1', source: 'apps/api/src/lib/training/training-lifecycle-events.js:emitTrainingCompletedIfNeeded', durable: true,
    },
    {
        name: 'training.overdue', label: { tr: 'Eğitim süresi doldu', en: 'Training overdue' },
        area: 'sla', kind: 'actionRequired', priority: 'high', module: 'training', category: 'sla',
        resource: { type: 'Training', pageKey: 'isoTraining' }, relation: 'assignedUser',
        relationLabel: { tr: 'Atanan kullanıcı', en: 'Assigned user' }, adapter: 'training.lifecycle.v1',
        guard: 'page-view.v1', source: 'packages/scheduled-jobs/src/training-overdue-scanner.js:scanTrainingOverdue', durable: true,
    },
]);

const MANAGED_CHANNEL_ORDER = Object.freeze(['in-app', 'email', 'sms', 'push']);

/**
 * Seed defaults for the policy editor: email on assignment / SLA / legal
 * reminders; SMS only on the small urgent BCM / privacy / SLA package.
 * `ticket.reassigned` is not a managed event — assignment email is `ticket.assigned`.
 */
export const MANAGED_DEFAULT_EMAIL_EVENTS = Object.freeze([
    'workflow.task.assigned',
    'workflow.task.slaWarning',
    'workflow.task.slaBreached',
    'workflow.task.slaEscalated',
    'ticket.assigned',
    'ticket.rejected',
    'ticket.returned',
    'ticket.resolved',
    'ticket.cancelled',
    'ticket.slaWarning',
    'ticket.slaBreached',
    'incident.rejected',
    'incident.returned',
    'incident.resolved',
    'incident.cancelled',
    'operationalRun.task.assigned',
    'operationalRun.task.reassigned',
    'operationalRun.task.deadlineWarning',
    'operationalRun.task.overdue',
    'training.assigned',
    'training.overdue',
    'dataBreach.deadlineOverdue',
    'dataBreach.dataSubjectDeadlineReminder',
    'dataBreach.dataSubjectDeadlineOverdue',
    'dataSubjectRequest.deadlineReminder',
    'dataSubjectRequest.deadlineOverdue',
    'dataProcessingAgreement.deadlineReminder',
    'dataProcessingAgreement.expired',
    'internationalTransfer.sscDeadlineReminder',
    'internationalTransfer.sscDeadlineOverdue',
    'disposalRun.periodicDue',
    'disposalRun.recordsExpired',
]);

export const MANAGED_DEFAULT_SMS_EVENTS = Object.freeze([
    'crisisEvent.declared',
    'bcp.activated',
    'dataBreach.deadlineOverdue',
    'dataBreach.dataSubjectDeadlineOverdue',
    'incident.slaBreached',
    'ticket.slaBreached',
    'workflow.task.slaEscalated',
]);

const MANAGED_DEFAULT_EMAIL_EVENT_SET = new Set(MANAGED_DEFAULT_EMAIL_EVENTS);
const MANAGED_DEFAULT_SMS_EVENT_SET = new Set(MANAGED_DEFAULT_SMS_EVENTS);

export function resolveManagedNotificationDefaultChannels(eventName, channelPolicy) {
    const allowed = new Set(channelPolicy?.allowed || MANAGED_CHANNEL_ORDER);
    const selected = new Set(channelPolicy?.required || []);
    if (allowed.has('in-app')) selected.add('in-app');
    if (MANAGED_DEFAULT_EMAIL_EVENT_SET.has(eventName)) selected.add('email');
    if (MANAGED_DEFAULT_SMS_EVENT_SET.has(eventName)) selected.add('sms');
    return MANAGED_CHANNEL_ORDER.filter((channel) => selected.has(channel) && allowed.has(channel));
}

function managedDefinitionFor(config) {
    if (!getEventDef(config.name)) {
        throw new Error(`[notification-routing] Managed event is missing from EVENT_CATALOG: ${config.name}`);
    }
    const resourceId = `fixture-${config.name.replaceAll('.', '-')}`;
    const nameField = config.resource.type === 'WorkflowTask' || config.resource.type === 'WorkflowInformedReceipt'
        ? 'nodeLabel'
        : config.resource.type === 'Ticket' || config.resource.type === 'Incident' ? 'title' : 'name';
    nameFieldByEvent.set(config.name, nameField);
    const relationDescription = {
        tr: 'Üretici adaptörünün aynı tenant içinde güvenli biçimde çözdüğü alıcı ilişkisi.',
        en: 'Recipient relation safely resolved within the tenant by the producer adapter.',
    };
    const safeFields = config.safeFields || [stringField('name', { tr: 'Görünen ad', en: 'Display name' }, { nullable: false, description: { tr: 'Kaydın güvenli görünen adı.', en: 'Safe display name of the record.' } })];
    const variables = config.variables ? [...config.variables] : [{
        key: 'name', type: 'string', label: { tr: 'Görünen ad', en: 'Display name' },
        description: { tr: 'Bildirim metnindeki güvenli kayıt adı.', en: 'Safe record name in notification copy.' },
        example: config.label.tr, required: true, maxLength: 240,
    }];
    if (!variables.some((variable) => variable.key === 'resourceId')) variables.push(resourceIdVariable(resourceId));
    const fixtureFields = config.fixtureFields || { name: config.label.en };
    const fixtureVariables = {
        name: config.label.en,
        ...Object.fromEntries(variables.map((variable) => [variable.key, variable.example])),
        resourceId,
    };
    const channelPolicy = config.channelPolicy || { required: ['in-app'], allowed: ['in-app', 'email', 'sms', 'push'] };
    const defaultPolicy = config.defaultPolicy || {
        audience: { include: [{ type: 'eventRelation', relation: config.relation }], exclude: [] },
        content: {
            mode: 'eventDefault',
            type: config.priority === 'urgent' ? 'error' : config.kind === 'actionRequired' ? 'warning' : 'info',
        },
        channels: ['in-app'],
        deliveryClass: config.kind === 'actionRequired' ? 'actionable' : 'informational',
        priority: config.priority,
        recipientCap: 500,
    };
    return NotificationEventDefinitionSchema.parse({
        name: config.name,
        version: 1,
        label: config.label,
        description: {
            tr: `${config.label.tr} olayı için güvenlik tarafından yönetilen bildirim sözleşmesi.`,
            en: `Safety-managed notification contract for the ${config.label.en.toLowerCase()} event.`,
        },
        module: config.module,
        category: config.category,
        notificationCategory: config.category,
        management: { status: 'managed', area: config.area, kind: config.kind, priority: config.priority },
        customization: 'full',
        allowTenantSuppression: false,
        contextAdapterKey: config.adapter,
        accessGuardKey: config.guard,
        verificationSourceTypes: config.name === 'role.assigned'
            ? ['synthetic', 'dispatch']
            : ['synthetic', 'record', 'dispatch'],
        resource: config.resource,
        producer: { kind: 'explicit', source: config.source, durable: config.durable },
        ...(config.runtimeOnlyVariables ? { runtimeOnlyVariables: config.runtimeOnlyVariables } : {}),
        safeFields,
        relations: [{ key: config.relation, label: config.relationLabel, description: relationDescription, maxMembers: 500 }],
        audienceSelectors: config.audienceSelectors || MANAGED_SPECIALTY_AUDIENCE_SELECTORS,
        variables,
        channelPolicy,
        defaultPolicy: {
            ...defaultPolicy,
            channels: resolveManagedNotificationDefaultChannels(config.name, channelPolicy),
        },
        maxRecipients: 500,
        previewFixture: {
            version: 1,
            event: { id: `fixture-${config.name}`, name: config.name, occurredAt: '2026-01-01T00:00:00.000Z', actorId: 'fixture-actor', correlationId: `fixture-${config.name}`, causationId: null },
            tenant: { id: 'fixture-tenant' },
            resource: { type: config.resource.type, id: resourceId, pageKey: config.resource.pageKey },
            fields: fixtureFields,
            relations: { [config.relation]: ['fixture-recipient'] },
            variables: fixtureVariables,
            navigation: { pageKey: config.resource.pageKey, entityId: resourceId, kind: 'listRowHighlight' },
        },
    });
}

const specialtyDefinitions = MANAGED_CONFIGS.map(managedDefinitionFor);
const definitions = [...historicalDefinitions, ...customDefinitions, ...detailDefinitions, ...specialtyDefinitions];

const byName = new Map();
for (const definition of definitions) {
    if (byName.has(definition.name)) {
        throw new Error(`[notification-routing] Duplicate curated event: ${definition.name}`);
    }
    byName.set(definition.name, Object.freeze(definition));
}

// Compatibility classification for the 42 CRUD definitions that pre-date
// managed routing. Their legacy DEFAULT_* persistence identities remain valid
// historical records even though the event contracts are now managed.
export const CRUD_COMPATIBILITY_NOTIFICATION_EVENT_DEFINITIONS = Object.freeze(
    historicalDefinitions.map((definition) => byName.get(definition.name)),
);
export const CRUD_COMPATIBILITY_NOTIFICATION_EVENT_NAMES = Object.freeze(
    CRUD_COMPATIBILITY_NOTIFICATION_EVENT_DEFINITIONS.map(({ name }) => name),
);
// Compatibility aliases for callers that still mean the 42 historical CRUD definitions.
export const NOTIFICATION_EVENT_DEFINITIONS = CRUD_COMPATIBILITY_NOTIFICATION_EVENT_DEFINITIONS;
export const HISTORICAL_NOTIFICATION_EVENT_NAMES = CRUD_COMPATIBILITY_NOTIFICATION_EVENT_NAMES;
export const PILOT_NOTIFICATION_EVENT_NAMES = CRUD_COMPATIBILITY_NOTIFICATION_EVENT_NAMES;
export const SPECIALTY_MANAGED_NOTIFICATION_EVENT_DEFINITIONS = Object.freeze(
    specialtyDefinitions.map((definition) => byName.get(definition.name)),
);
export const SPECIALTY_MANAGED_NOTIFICATION_EVENT_NAMES = Object.freeze(
    SPECIALTY_MANAGED_NOTIFICATION_EVENT_DEFINITIONS.map(({ name }) => name),
);
// Ownership is the authoritative promotion seam. The definition builders above
// provide the schema payload; this filter prevents a definition from becoming
// managed merely because it exists in the catalog.
export const MANAGED_NOTIFICATION_EVENT_DEFINITIONS = Object.freeze(
    definitions.filter(({ name }) => POLICY_OWNERSHIP_EVENT_SET.has(name)),
);
export const MANAGED_NOTIFICATION_EVENT_NAMES = Object.freeze(
    MANAGED_NOTIFICATION_EVENT_DEFINITIONS.map(({ name }) => name),
);

if (MANAGED_NOTIFICATION_EVENT_NAMES.some((name) => !isPolicyOwnedNotificationEvent(name))) {
    throw new Error('[notification-routing] managed definitions must be ownership-registry rows');
}

const historicalNameSet = new Set(HISTORICAL_NOTIFICATION_EVENT_NAMES);

function policyCodeToken(eventName) {
    return String(eventName)
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/\./g, '_')
        .toUpperCase();
}

export function managedNotificationPolicySystemCode(eventName) {
    if (!MANAGED_NOTIFICATION_EVENT_NAMES.includes(eventName)) {
        throw new TypeError(`Notification event is not managed: ${eventName}`);
    }
    return `${historicalNameSet.has(eventName) ? 'MANAGED_DEFAULT_' : 'DEFAULT_'}${policyCodeToken(eventName)}`;
}

export function managedNotificationPolicyTenantCode(eventName) {
    if (!MANAGED_NOTIFICATION_EVENT_NAMES.includes(eventName)) {
        throw new TypeError(`Notification event is not managed: ${eventName}`);
    }
    return `MANAGED_TENANT_${policyCodeToken(eventName)}`.slice(0, 120);
}

export function getNotificationEventDefinition(name) {
    return byName.get(name) || null;
}

export function listNotificationEventDefinitions() {
    return [...NOTIFICATION_EVENT_DEFINITIONS];
}

export function listSpecialtyManagedNotificationEventDefinitions() {
    return [...SPECIALTY_MANAGED_NOTIFICATION_EVENT_DEFINITIONS];
}

export function listManagedNotificationEventDefinitions() {
    return [...MANAGED_NOTIFICATION_EVENT_DEFINITIONS];
}

export function isCuratedNotificationEvent(name) {
    return byName.has(name);
}

export function isManagedNotificationEvent(name) {
    return MANAGED_NOTIFICATION_EVENT_NAMES.includes(name);
}

export function getNotificationEventNameField(eventName) {
    return nameFieldByEvent.get(eventName) || 'name';
}
